import React, { useState, useEffect, useRef } from "react";
import TVChart from "./TVChart";

// ============================================================================
// Утилиты
// ============================================================================

// Небольшая пауза, чтобы переходы выглядели как на «живом» нагруженном сайте,
// а не срабатывали абсолютно мгновенно.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Меняет путь в адресной строке без перезагрузки страницы (навигация «для вида»).
function setUrlPath(path) {
  try {
    if (window.location.pathname !== path) {
      window.history.replaceState(null, "", path);
    }
  } catch (e) {}
}

// Разбирает символ вида "BYBIT:BTCUSDT.P" -> { pair, category, market }
// Префиксы: "BYBIT:" (или без префикса) = крипта; "FX:" = форекс; "STOCK:" = акции.
// ".P" = бессрочный фьючерс (linear), без суффикса = спот.
function parseSymbol(symbol) {
  const hasPrefix = symbol.includes(":");
  const prefix = hasPrefix ? symbol.split(":")[0].toUpperCase() : "BYBIT";
  const raw = hasPrefix ? symbol.split(":")[1] : symbol;
  if (prefix === "FX") {
    return { pair: raw.toUpperCase(), category: "forex", market: "forex" };
  }
  if (prefix === "STOCK") {
    return { pair: raw.toUpperCase(), category: "stock", market: "stock" };
  }
  const isFutures = /\.P$/i.test(raw);
  const pair = raw.replace(/\.P$/i, "").toUpperCase();
  return { pair, category: isFutures ? "linear" : "spot", market: "crypto" };
}

function decimalsFor(p) {
  if (!isFinite(p)) return 2;
  const abs = Math.abs(p);
  if (abs >= 1000) return 1;
  if (abs >= 100) return 2;
  if (abs >= 1) return 3;
  if (abs >= 0.01) return 5;
  return 8;
}

// Форматирование под цену инструмента (кол-во знаков зависит от масштаба цены)
const fmt = (n) => {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  const d = decimalsFor(n);
  return n.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};

// Форматирование под деньги (USDT), всегда 2 знака
const fmt2 = (n) => {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Курс USDT->EUR. Приблизительный, фиксированный (демо; при желании можно
// динамически подтягивать с сервера/ecb.europa.eu). 1 USDT ≈ 0.92 EUR.
const USDT_TO_EUR = 0.92;
const fmtEur = (usdt) => fmt2((usdt || 0) * USDT_TO_EUR);

// Комиссии по деривативам: тейкер 0.055%, мейкер 0.02%.
// Берутся от стоимости позиции с учётом плеча (кол-во × цена × плечо)
// при открытии И при закрытии — как на реальной бирже.
const TAKER_FEE = 0.00055;
const MAKER_FEE = 0.0002;

const fmtQty = (n) => {
  if (n === null || n === undefined || !isFinite(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 4 });
};

function fmtCountdown(ms) {
  if (ms === null || ms <= 0 || !isFinite(ms)) return "--:--:--";
  const totalSec = Math.floor(ms / 1000);
  const h = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const s = String(totalSec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// "1s" убран: kline-API отдаёт минимум минутные свечи
const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d", "1w", "1M"];
const LEVERAGES = [5, 10, 20, 25, 50, 75, 100];

// Иконка КРИПТО-монеты по базовому тикеру пары (например "ADAUSDT" -> "ada").
// Возвращает НЕСКОЛЬКО источников по очереди, чтобы «пустых» иконок не оставалось.
function coinIconSources(basePair) {
  const t = basePair.replace(/\.P$/i, "").replace(/USDT$/i, "");
  // фьючерсные тикеры вида "1000PEPE"/"10000SATS" — иконка ищется по «чистому» имени
  const clean = t.replace(/^1000+/, "");
  const lc = t.toLowerCase();
  const lcClean = clean.toLowerCase();
  return [
    // CoinCap — большой и хорошо поддерживаемый набор логотипов монет
    `https://assets.coincap.io/assets/icons/${lc}@2x.png`,
    // cryptofonts — цветные SVG почти на все тикеры
    `https://cryptofonts.com/img/icons/${lc}.svg`,
    // Набор иконок биржи — покрывает большинство торгуемых пар
    `https://s1.bycsi.com/app/assets/token/${lc}.svg`,
    // Публичный набор spothq
    `https://cdn.jsdelivr.net/gh/spothq/cryptocurrency-icons@master/128/color/${lc}.png`,
    // те же источники, но по «очищенному» имени (для 1000PEPE и т.п.)
    `https://assets.coincap.io/assets/icons/${lcClean}@2x.png`,
    `https://cryptofonts.com/img/icons/${lcClean}.svg`,
    // Запасной набор
    `https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@master/32/color/${lc}.png`,
  ];
}

// Источники логотипа акции по тикеру (например "AAPL").
function stockIconSources(ticker) {
  const up = ticker.toUpperCase();
  return [
    `https://financialmodelingprep.com/image-stock/${up}.png`,
    `https://s3-symbol-logo.tradingview.com/${up.toLowerCase()}.svg`,
  ];
}

// Флаг страны по коду валюты (для форекс-пар)
function currencyFlag(cur) {
  const cc = CURRENCY_COUNTRY[cur.toUpperCase()];
  return cc ? `https://flagcdn.com/w40/${cc}.png` : null;
}

// Кружок с буквой — универсальный запасной вариант, если картинка не загрузилась.
function LetterCircle({ label, size }) {
  const s = (label || "?").replace(/[^A-Za-z0-9]/g, "");
  let hsum = 0;
  for (let i = 0; i < s.length; i++) hsum = (hsum * 31 + s.charCodeAt(i)) % 360;
  return (
    <span
      style={{ width: size, height: size, fontSize: size * 0.5, backgroundColor: `hsl(${hsum},55%,40%)` }}
      className="rounded-full inline-flex items-center justify-center text-white font-bold shrink-0"
    >
      {s[0]?.toUpperCase() || "?"}
    </span>
  );
}

// Иконка форекс-пары: два флага стран валют (EUR/USD -> флаг ЕС + флаг США).
function ForexIcon({ pair, size }) {
  const [base, quote] = pair.split("/");
  const f1 = currencyFlag(base || "");
  const f2 = currencyFlag(quote || "");
  if (!f1 && !f2) return <LetterCircle label={base} size={size} />;
  const d = Math.round(size * 0.68); // диаметр одного флага
  const flagImg = (src) => (
    <img
      src={src}
      alt=""
      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
      style={{ width: d, height: d }}
      className="rounded-full object-cover border border-slate-900 block"
    />
  );
  return (
    <span style={{ width: size, height: size }} className="relative inline-block shrink-0">
      {f1 && (
        <span className="absolute" style={{ left: 0, top: 0 }}>{flagImg(f1)}</span>
      )}
      {f2 && (
        <span className="absolute" style={{ right: 0, bottom: 0 }}>{flagImg(f2)}</span>
      )}
    </span>
  );
}

// Иконка инструмента с многоуровневым запасным вариантом. Тип определяется по `market`
// (crypto|forex|stock) или выводится из символа: "/" -> форекс, "USDT" -> крипта.
function CoinIcon({ pair, size = 16, market }) {
  const kind = market || (pair.includes("/") ? "forex" : /USDT/i.test(pair) ? "crypto" : "stock");
  const sources =
    kind === "forex" ? [] : kind === "stock" ? stockIconSources(pair) : coinIconSources(pair);
  const label =
    kind === "crypto" ? pair.replace(/\.P$/i, "").replace(/USDT$/i, "") : pair;
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [pair]);

  if (kind === "forex") return <ForexIcon pair={pair} size={size} />;
  if (idx >= sources.length || !label) return <LetterCircle label={label} size={size} />;
  return (
    <img
      src={sources[idx]}
      onError={() => setIdx((i) => i + 1)}
      alt={label}
      style={{ width: size, height: size }}
      className="rounded-full shrink-0 object-contain bg-slate-800"
    />
  );
}

// Полный список торговых пар (все USDT-пары: спот и бессрочные фьючерсы).
// Загружается один раз и кэшируется на время сессии; при ошибке сети — запасной список.
const PAIRS_CACHE = { spot: null, linear: null };
async function fetchAllPairs(category) {
  if (PAIRS_CACHE[category]) return PAIRS_CACHE[category];
  const out = [];
  let cursor = "";
  try {
    for (let page = 0; page < 5; page++) {
      const res = await fetch(
        `https://api.bybit.com/v5/market/instruments-info?category=${category}&limit=1000${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`
      );
      const json = await res.json();
      const list = json?.result?.list || [];
      list.forEach((x) => {
        if (x.quoteCoin === "USDT" && x.status === "Trading") out.push(x.symbol);
      });
      cursor = json?.result?.nextPageCursor || "";
      if (!cursor || list.length === 0) break;
    }
  } catch (e) {}
  const result = out.length > 0 ? out : PAIR_LIST;
  PAIRS_CACHE[category] = result;
  return result;
}

// Список пар для собственного селектора инструмента (не встроенный поиск TradingView).
// Один и тот же список используется и для спота, и для фьючерсов — суффикс ".P" добавляется
// автоматически в зависимости от выбранной вкладки.
const PAIR_LIST = [
  "BTCUSDT", "ETHUSDT", "SOLUSDT", "XRPUSDT", "BNBUSDT", "DOGEUSDT", "ADAUSDT",
  "AVAXUSDT", "LINKUSDT", "TONUSDT", "TRXUSDT", "DOTUSDT", "MATICUSDT", "LTCUSDT",
  "BCHUSDT", "NEARUSDT", "APTUSDT", "ARBUSDT", "OPUSDT", "ATOMUSDT", "FILUSDT",
  "ICPUSDT", "INJUSDT", "SUIUSDT", "SEIUSDT", "TIAUSDT", "RNDRUSDT", "FETUSDT",
  "PEPEUSDT", "SHIBUSDT", "WIFUSDT", "BONKUSDT", "UNIUSDT", "AAVEUSDT", "MKRUSDT",
  "ETCUSDT", "XLMUSDT", "HBARUSDT", "VETUSDT", "ALGOUSDT",
];

// Форекс и акции берутся с Yahoo Finance через прокси server.js (Bybit их не
// отдаёт, а Yahoo блокирует прямые запросы из браузера). Ключ не нужен, лимитов
// практически нет. Один запрос отдаёт и текущую цену, и свечи для графика.
// Форекс: EUR/USD -> EURUSD=X (символ Yahoo). Акции: BRK.B -> BRK-B.
function yahooSymbol(pair, market) {
  if (market === "forex") return pair.replace("/", "").toUpperCase() + "=X";
  return pair.replace(/\./g, "-").toUpperCase();
}

// Популярные валютные пары (форекс). Внутренний формат — "EUR/USD" (для Yahoo → EURUSD=X).
const FOREX_LIST = [
  // Мажоры
  "EUR/USD", "GBP/USD", "USD/JPY", "USD/CHF", "AUD/USD", "USD/CAD", "NZD/USD",
  // Кросс-курсы EUR
  "EUR/GBP", "EUR/JPY", "EUR/CHF", "EUR/AUD", "EUR/CAD", "EUR/NZD", "EUR/SGD",
  "EUR/NOK", "EUR/SEK", "EUR/PLN", "EUR/HUF", "EUR/CZK", "EUR/TRY", "EUR/ZAR",
  // Кросс-курсы GBP
  "GBP/JPY", "GBP/CHF", "GBP/AUD", "GBP/CAD", "GBP/NZD",
  // Кросс-курсы JPY
  "AUD/JPY", "CAD/JPY", "CHF/JPY", "NZD/JPY",
  // Кросс-курсы AUD / NZD / CAD
  "AUD/CAD", "AUD/CHF", "AUD/NZD", "NZD/CAD", "NZD/CHF", "CAD/CHF",
  // Экзотика с USD
  "USD/CNH", "USD/SGD", "USD/HKD", "USD/MXN", "USD/ZAR", "USD/TRY",
  "USD/NOK", "USD/SEK", "USD/DKK", "USD/PLN", "USD/HUF", "USD/CZK",
  "USD/THB", "USD/INR", "USD/RUB", "USD/BRL", "USD/ILS",
];

// Популярные акции (тикеры бирж США, символы Yahoo Finance).
const STOCK_LIST = [
  // Мегакапы / техи
  "AAPL", "MSFT", "GOOGL", "GOOG", "AMZN", "NVDA", "META", "TSLA", "NFLX", "AVGO",
  "AMD", "INTC", "ORCL", "CSCO", "ADBE", "CRM", "QCOM", "TXN", "IBM", "MU",
  "AMAT", "PYPL", "SHOP", "UBER", "ABNB", "PLTR", "SNOW", "COIN", "SQ", "SPOT",
  // Потребительский сектор / ритейл
  "DIS", "KO", "PEP", "MCD", "NKE", "SBUX", "WMT", "COST", "TGT", "HD",
  "LOW", "PG", "CL", "MDLZ", "PM",
  // Финансы
  "JPM", "BAC", "WFC", "GS", "MS", "C", "V", "MA", "AXP", "BLK", "BRK.B",
  // Здоровье / энергетика / промышленность
  "JNJ", "PFE", "MRK", "ABBV", "UNH", "LLY", "XOM", "CVX", "BA", "CAT", "GE",
  // Авто / китайские / прочее
  "F", "GM", "RIVN", "BABA", "NIO", "PDD", "JD", "T", "VZ", "TMUS",
];

// ISO-код страны для флага по валюте (для иконок форекс-пар)
const CURRENCY_COUNTRY = {
  USD: "us", EUR: "eu", GBP: "gb", JPY: "jp", CHF: "ch", AUD: "au", CAD: "ca",
  NZD: "nz", CNH: "cn", CNY: "cn", SGD: "sg", HKD: "hk", MXN: "mx", ZAR: "za",
  TRY: "tr", SEK: "se", NOK: "no", PLN: "pl", RUB: "ru", INR: "in",
  DKK: "dk", HUF: "hu", CZK: "cz", THB: "th", BRL: "br", ILS: "il",
};

// Инструменты рисования собственного графика (TVChart на lightweight-charts).
// id соответствует TVChart.selectTool(id); после завершения фигуры инструмент
// автоматически сбрасывается на курсор (TVChart сообщает об этом через onToolChange).
const DRAW_TOOLS = [
  { id: "cursor", svg: "noDraw",           titleKey: "toolCursor" },
  { id: "trend",  icon: "pi-chart-line",   titleKey: "toolTrend" },
  { id: "ray",    svg: "ray",              titleKey: "toolRay" },
  { id: "hline",  icon: "pi-minus",        titleKey: "toolHline" },
  { id: "rect",   icon: "pi-stop",         titleKey: "toolRect" },
  { id: "pencil", icon: "pi-pencil",       titleKey: "toolPencil" },
  { id: "text",   icon: "pi-tag",          titleKey: "toolText" },
  { id: "ruler",  icon: "pi-arrows-v",     titleKey: "toolRuler" },
  { id: "eraser", icon: "pi-eraser",       titleKey: "toolEraser" },
];

// Палитра цветов рисования
const DRAW_COLORS = ["#60a5fa", "#22c55e", "#ef4444", "#1e40af", "#a855f7", "#eab308", "#ffffff", "#737373"];

// Приводит ввод к безопасному числовому виду: разрешает запятую как десятичный разделитель,
// схлопывает повторные точки, отсекает всё лишнее
function sanitizeNumber(raw) {
  let s = raw.replace(",", ".").replace(/[^0-9.]/g, "");
  const parts = s.split(".");
  if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
  return s;
}

// ============================================================================
// API общего сервера пользователей (server.js).
// Приоритет: 1) REACT_APP_API_URL, если задан при сборке;
//            2) в режиме разработки (npm start, порт 3000) — localhost:4000;
//            3) иначе — тот же адрес, с которого открыт сайт (server.js раздаёт
//               и сайт, и API по одной ссылке, например через ngrok).
// ============================================================================
const API_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.port === "3000" ? "http://localhost:4000" : "");

async function apiFetch(path, { token, method = "GET", body } = {}) {
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      // Обязательно для бесплатного ngrok: без этого заголовка ngrok отдаёт
      // HTML-страницу с предупреждением вместо ответа сервера, и все запросы ломаются
      "ngrok-skip-browser-warning": "true",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let json = null;
  try {
    json = await res.json();
  } catch (e) {}
  if (!res.ok) {
    const err = new Error(json?.error || `Ошибка сервера (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}

// ============================================================================
// Хук: реальная цена и рыночные данные с публичного рыночного API — сразу для НЕСКОЛЬКИХ
// инструментов одновременно (текущий график + все пары, по которым есть открытые
// позиции/ордера — даже если сейчас на графике показана другая пара)
// ============================================================================
function useBybitTickers(symbols) {
  const [map, setMap] = useState({});
  const symbolsRef = useRef(symbols);
  symbolsRef.current = symbols;
  // время последнего успешного опроса по каждому форекс/акция-символу (для троттлинга)
  const lastTdRef = useRef({});

  useEffect(() => {
    let active = true;

    async function fetchOne(sym) {
      const { pair, category, market } = parseSymbol(sym);
      // --- Форекс и акции: Yahoo Finance через прокси server.js (Bybit их не отдаёт) ---
      if (market === "forex" || market === "stock") {
        try {
          const ySym = yahooSymbol(pair, market);
          const res = await fetch(
            `${API_URL}/api/market/yahoo?symbol=${encodeURIComponent(ySym)}&interval=5m&range=1d`,
            { headers: { "ngrok-skip-browser-warning": "true" } }
          );
          const j = await res.json();
          const meta = j?.chart?.result?.[0]?.meta;
          if (!meta || meta.regularMarketPrice == null) return null;
          const last = parseFloat(meta.regularMarketPrice);
          const prev = parseFloat(meta.chartPreviousClose ?? meta.previousClose ?? last);
          const pct = prev ? ((last - prev) / prev) * 100 : 0;
          return {
            pair,
            category,
            market,
            lastPrice: last,
            indexPrice: last,
            markPrice: last,
            high24h: parseFloat(meta.regularMarketDayHigh ?? last),
            low24h: parseFloat(meta.regularMarketDayLow ?? last),
            turnover24h: meta.regularMarketVolume != null ? parseFloat(meta.regularMarketVolume) : null,
            changePct: pct,
            fundingRate: null,
            nextFundingTime: null,
          };
        } catch (e) {
          return null;
        }
      }
      // --- Крипта: Bybit ---
      try {
        const res = await fetch(
          `https://api.bybit.com/v5/market/tickers?category=${category}&symbol=${pair}`
        );
        const json = await res.json();
        const t = json?.result?.list?.[0];
        if (!t) return null;
        return {
          pair,
          category,
          market: "crypto",
          lastPrice: parseFloat(t.lastPrice),
          indexPrice: parseFloat(t.indexPrice || t.lastPrice),
          markPrice: parseFloat(t.markPrice || t.lastPrice),
          high24h: parseFloat(t.highPrice24h),
          low24h: parseFloat(t.lowPrice24h),
          turnover24h: parseFloat(t.turnover24h),
          changePct: parseFloat(t.price24hPcnt) * 100,
          fundingRate: t.fundingRate ? parseFloat(t.fundingRate) * 100 : null,
          nextFundingTime: t.nextFundingTime ? parseInt(t.nextFundingTime, 10) : null,
        };
      } catch (e) {
        return null;
      }
    }

    async function fetchAll() {
      const list = Array.from(new Set(symbolsRef.current));
      const results = await Promise.all(
        list.map(async (sym) => {
          const { market } = parseSymbol(sym);
          // Yahoo Finance (через прокси) не имеет жёстких лимитов, но данные по
          // форекс/акциям обновляются не каждую секунду — опрашиваем раз в 3 c,
          // чтобы не гонять сервер зря (крипта с Bybit — каждую секунду).
          if (market === "forex" || market === "stock") {
            const now = Date.now();
            const last = lastTdRef.current[sym] || 0;
            if (now - last < 3000) return [sym, null]; // ещё рано — пропускаем
            lastTdRef.current[sym] = now;
          }
          return [sym, await fetchOne(sym)];
        })
      );
      if (!active) return;
      setMap((prev) => {
        const next = { ...prev };
        results.forEach(([sym, data]) => {
          if (data) next[sym] = data;
        });
        return next;
      });
    }

    fetchAll();
    const id = setInterval(fetchAll, 1000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [JSON.stringify(symbols)]); // eslint-disable-line react-hooks/exhaustive-deps

  return map;
}

// ============================================================================
// Локализация (RU / EN / DE) — покрывает основные подписи интерфейса.
// Добавить новую строку: впишите ключ во все три языка ниже и используйте t('ключ').
// ============================================================================
const DICT = {
  ru: {
    // RU локаль отключена — оставлена пустой, чтобы существующие обращения к DICT.ru не падали
  },
  en: {
    spot: "Spot", perpetual: "USDT Perpetual", indexPrice: "Index Price", change24h: "24h Change",
    high24h: "24h High", low24h: "24h Low", turnover24h: "24h Turnover(USDT)", fundingCountdown: "Funding / Countdown",
    chart: "Chart", overview: "Overview", data: "Data", news: "News Feed",
    orderBook: "Order Book", price: "Price(USDT)", qtyCol: "Qty", totalCol: "Total",
    limit: "Limit", market: "Market", limitOco: "Limit (OCO)...",
    available: "Available", quantity: "Quantity", amount: "Amount",
    takeProfit: "Take Profit", stopLoss: "Stop Loss", tpSl: "TP/SL",
    buyLong: "Buy / Long", sellShort: "Sell / Short", leverage: "Leverage",
    processing: "Processing…",
    openOrders: "Open Orders", positions: "Positions", orderHistory: "Order History",
    tradeHistory: "Trade History", assets: "Assets", loans: "Loans", tools: "Tools",
    noOpenOrders: "You have no open orders.", noPositions: "You have no open positions.",
    cancel: "Cancel", close: "Close", confirm: "Confirm",
    login: "Log In", register: "Register", logout: "Log Out", username: "Username", password: "Password",
    emailWord: "Email", loginOrEmail: "Username or email", accountId: "Account ID", profileWord: "Profile",
    admin: "Admin", users: "Users", grantAdmin: "Grant Admin", revokeAdmin: "Revoke Admin",
    distributeFunds: "Add Funds", contactAdmin: "Only an admin can add funds.",
    pnlTab: "P&L", userPositions: "User Positions", trade: "Trade",
    lastPrice: "Last Price", markPrice: "Mark Price", fundingRate: "Funding Rate",
    spotPair: "spot pair", perpContract: "perpetual contract",
    loadingInstrument: "Loading instrument data…", loadingData: "Loading data…",
    loadingNews: "Loading news…", newsError: "Failed to load news. Check your connection.",
    futures: "Futures", searchPair: "Search pair, e.g. BTC", nothingFound: "Nothing found",
    forexWord: "Forex", stocksWord: "Stocks",
    customTicker: "Custom ticker (e.g. SANDUSDT)", loadingPairs: "Loading pair list…",
    pairCol: "Pair", typeCol: "Type", sideCol: "Side", statusCol: "Status", entryCol: "Entry",
    exitCol: "Exit", levCol: "Lev", reasonCol: "Reason", timeCol: "Time", userCol: "User",
    roleCol: "Role", balanceCol: "Balance",
    posContract: "Contracts", posValue: "Value", posMark: "Mark Price",
    posLiq: "Liq. Price", posBreakeven: "Breakeven Price", posIM: "IM", posMM: "MM",
    posUpnl: "Unrealized P&L(%)", posRealized: "Realized P&L", posCloseAs: "Close by",
    posEntryPrice: "Entry Price", closeLimit: "Limit", closeMarket: "Market",
    long: "Long", short: "Short", buyWord: "Buy", sellWord: "Sell",
    emptyOrderHistory: "Order history is empty.", emptyTradeHistory: "Trade history is empty.",
    balanceWord: "Balance", inMargin: "In Margin", availWord: "Available",
    totalAssets: "Total Assets", unifiedAccount: "Unified Trading Account", marginAccount: "Margin Balance",
    unifiedHint: "Deposits and withdrawals", transferWord: "Transfer", maxWord: "Max",
    fromWord: "From", toWord: "To", swapDir: "Swap direction",
    confirmTransfer: "Confirm transfer", transferDone: "Transfer completed",
    totalPnl: "Total realized PnL", noClosedTrades: "No closed trades yet.",
    sectionStub: ".", sectionWord: "Section",
    marginMode: "Margin mode", isolated: "Isolated", chooseLev: "Choose leverage",
    contractData: "Contract Details", expiry: "Expiry", perpWord: "Perpetual", priceWord: "Price",
    minLev: "Min Leverage", maxLev: "Max Leverage", showWord: "Show", hideWord: "Hide", notSet: "Not set",
    stFilled: "Filled", stPlaced: "Placed", stCanceled: "Canceled",
    rManual: "Manual", rOpen: "Open", rOpenLimit: "Open (limit)", rClose: "Close",
    tPriceNotLoaded: "Price not loaded yet, wait a second",
    tEnterQty: "Enter a quantity before placing an order", tBadPrice: "Invalid price",
    tNeedMargin: "Insufficient available balance (need ≈", tTpBadPrice: "Enter a valid Take Profit price",
    tSlBadPrice: "Enter a valid Stop Loss price",
    tTpBuy: "TP for a long must be above entry", tTpSell: "TP for a short must be below entry",
    tSlBuy: "SL for a long must be below entry", tSlSell: "SL for a short must be above entry",
    tOrderCanceled: "Order canceled", tLongOpened: "Long opened", tShortOpened: "Short opened",
    tLimitPlacedBuy: "Buy limit order placed at", tLimitPlacedSell: "Sell limit order placed at",
    tLimitFilled: "Limit order filled at", tPosClosed: "Position closed",
    tClosedBy: "Position closed by", tDrawingsCleared: "All drawings removed",
    tChartSettings: "Chart settings", tUserPosClosed: "User position closed",
    csVolume: "Show volume", csLogScale: "Logarithmic scale", csFit: "Fit to screen",
    tTpSlSet: "Position TP/SL updated", saveWord: "Save", editTpSl: "Edit TP/SL",
    userHistory: "User History", noUserHistory: "Users have no trade history yet.",
    allUsersWord: "All", feeCol: "Fee",
    statTrades: "Total trades", statWins: "Wins", statLosses: "Losses",
    statGrossProfit: "Gross profit", statGrossLoss: "Gross loss", statNetPnl: "Net PnL",
    statAvgWin: "Avg win", statAvgLoss: "Avg loss", statProfitFactor: "Profit factor",
    statFees: "Total fees",
    userTransactions: "User Transactions", txHistory: "Transaction History", noTransactions: "No transactions yet.",
    assetsTitle: "My Assets", positionHistory: "Position History",
    depositWord: "Deposit", withdrawWord: "Withdraw", closeWord: "Close",
    depositAmount: "Deposit amount", withdrawAmount: "Withdraw amount",
    cardDetails: "Card details", cardNumber: "Card number", cardExpiry: "Expiry", cardHolder: "Cardholder",
    payMethod: "Deposit method", methodCard: "Card", ibanDetails: "IBAN details", ibanHolder: "Account holder", ibanInvalid: "Invalid IBAN",
    methodCrypto: "Crypto", cryptoNetwork: "Network", cryptoAddress: "Wallet address", cryptoAddressPlaceholder: "Enter your wallet address",
    cryptoDepositAddress: "Send to this address", cryptoDepositNote: "Send exactly the specified amount to the address below. After sending, submit the request for admin confirmation.",
    cryptoWithdrawNote: "Enter your wallet address. Funds will be sent after admin confirmation.",
    ibanFirstName: "First name", ibanLastName: "Last name",
    ibanDepositTitle: "Bank transfer details", ibanDepositBank: "Bank",
    ibanDepositNote: "Transfer the amount to the account below, then submit the request for admin confirmation.",
    ibanNotConfigured: "Bank details are not available yet. Please contact support.",
    adminIbanTitle: "Deposit IBAN (shown to users)", adminIbanSave: "Save",
    adminIbanSaved: "Deposit details saved", backdateLabel: "Display date",
    confirmDeposit: "Deposit", confirmWithdraw: "Withdraw funds",
    fundNote: "Requests are processed by an admin manually. Funds are credited after confirmation.",
    amountCol: "Amount", cardCol: "Details", actionCol: "Action",
    txPending: "Pending", txDone: "Success", txRejected: "Rejected",
    approveWord: "Approve", rejectWord: "Reject", onlyPending: "Pending only",
    tDepositReq: "Deposit request created", tWithdrawReq: "Withdrawal request created",
    tTxApproved: "Transaction approved", tTxRejected: "Transaction rejected",
    tInsuffWithdraw: "Withdrawal amount exceeds available balance",
    availToBorrow: "Available to borrow", loanRate: "Rate", loanNote: "Loans against your assets. The rate may change.",
    priceUnit: "Price", tpSlUnitHint: "Toggle input: price / percent (ROI incl. leverage)",
    tGranted: "Granted", tGrantedTo: "to user", tDrawHint: "— draw with your mouse on the chart",
    viewAll: "View: all", viewSells: "View: sells only", viewBuys: "View: buys only",
    insuffWarn1: "Insufficient available balance: need ≈", insuffWarn2: "available",
    insuffWarn3: "Transfer funds from your Unified Trading Account to your Margin Balance.",
    insuffWarn4: "Deposit funds to your Unified Trading Account, then transfer them to your Margin Balance.",
    adminUsersInfo1: "Total users on server:", adminUsersInfo2: "The list refreshes every 5 seconds.",
    adminUsersError: "Failed to load user list:",
    adminUsersHint: "Check that server.js is running and the site is opened via the ngrok tunnel URL.",
    adminUsersLoading: "Loading user list…",
    adminPosInfo: "Open positions of all users. Auto-refreshing. Closing is invisible to the user.",
    noUserPositions: "Nobody has open positions right now.",
    authFill: "Fill in username and password",
    authFooter1: "Accounts are stored on a shared server — log in from any device.",
    authFooter2: "The first registered user automatically becomes an admin.",
    toolCursor: "Cursor", toolTrend: "Trend line (2 points)", toolRay: "Ray",
    toolHline: "Horizontal level (1 click)", toolRect: "Rectangle",
    toolPencil: "Pencil (free drawing)", toolText: "Text label",
    toolRuler: "Ruler (Δ price / % / bars)", toolSearch: "Search trading pair", toolTrash: "Delete all drawings",
    toolEraser: "Eraser (delete object)", toolColor: "Drawing color", tEraserHint: "Tap an object to delete it",
    dragW: "Drag to resize. Double-click to reset",
    dragH: "Drag to resize the panel. Double-click to reset",
    dragB: "Drag to resize the order book. Double-click to reset",
    pickPairTitle: "Choose trading pair",
    connecting: "Connecting to server…", triggerPrice: "Trigger price", noData: "no data",
    indexPriceShort: "Index Price",
    serverUnavailable: "Server unavailable. Make sure server.js is running.",
  },
  de: {
    spot: "Spot", perpetual: "USDT Perpetual", indexPrice: "Indexpreis", change24h: "24h Änderung",
    high24h: "24h Hoch", low24h: "24h Tief", turnover24h: "24h Umsatz(USDT)", fundingCountdown: "Finanzierung / Countdown",
    chart: "Chart", overview: "Übersicht", data: "Daten", news: "Nachrichten",
    orderBook: "Orderbuch", price: "Preis(USDT)", qtyCol: "Menge", totalCol: "Gesamt",
    limit: "Limit", market: "Markt", limitOco: "Limit (OCO)...",
    available: "Verfügbar", quantity: "Menge", amount: "Betrag",
    takeProfit: "Take Profit", stopLoss: "Stop Loss", tpSl: "TP/SL",
    buyLong: "Kaufen / Long", sellShort: "Verkaufen / Short", leverage: "Hebel",
    processing: "Wird verarbeitet…",
    openOrders: "Offene Orders", positions: "Positionen", orderHistory: "Orderverlauf",
    tradeHistory: "Handelsverlauf", assets: "Guthaben", loans: "Kredite", tools: "Werkzeuge",
    noOpenOrders: "Keine offenen Orders.", noPositions: "Keine offenen Positionen.",
    cancel: "Stornieren", close: "Schließen", confirm: "Bestätigen",
    login: "Anmelden", register: "Registrieren", logout: "Abmelden", username: "Benutzername", password: "Passwort",
    emailWord: "E-Mail", loginOrEmail: "Benutzername oder E-Mail", accountId: "Konto-ID", profileWord: "Profil",
    admin: "Administrator", users: "Benutzer", grantAdmin: "Admin-Rechte geben", revokeAdmin: "Admin-Rechte entziehen",
    distributeFunds: "Guthaben hinzufügen", contactAdmin: "Nur ein Administrator kann Guthaben hinzufügen.",
    pnlTab: "P&L", userPositions: "Benutzer-Positionen", trade: "Handel",
    lastPrice: "Letzter Preis", markPrice: "Mark-Preis", fundingRate: "Finanzierungsrate",
    spotPair: "Spot-Paar", perpContract: "Perpetual-Kontrakt",
    loadingInstrument: "Instrumentdaten werden geladen…", loadingData: "Daten werden geladen…",
    loadingNews: "Nachrichten werden geladen…", newsError: "Nachrichten konnten nicht geladen werden.",
    futures: "Futures", searchPair: "Paar suchen, z.B. BTC", nothingFound: "Nichts gefunden",
    forexWord: "Forex", stocksWord: "Aktien",
    customTicker: "Eigener Ticker (z.B. SANDUSDT)", loadingPairs: "Paarliste wird geladen…",
    pairCol: "Paar", typeCol: "Typ", sideCol: "Seite", statusCol: "Status", entryCol: "Einstieg",
    exitCol: "Ausstieg", levCol: "Hebel", reasonCol: "Grund", timeCol: "Zeit", userCol: "Benutzer",
    roleCol: "Rolle", balanceCol: "Guthaben",
    posContract: "Kontrakte", posValue: "Wert", posMark: "Markierungspreis",
    posLiq: "Liq.-Preis", posBreakeven: "Break-even-Preis", posIM: "IM", posMM: "MM",
    posUpnl: "Unreal. P&L(%)", posRealized: "Realisierter P&L", posCloseAs: "Schließen per",
    posEntryPrice: "Einstiegspreis", closeLimit: "Limit", closeMarket: "Markt",
    long: "Long", short: "Short", buyWord: "Kauf", sellWord: "Verkauf",
    emptyOrderHistory: "Orderverlauf ist leer.", emptyTradeHistory: "Handelsverlauf ist leer.",
    balanceWord: "Guthaben", inMargin: "In Margin", availWord: "Verfügbar",
    totalAssets: "Gesamtvermögen", unifiedAccount: "Unified Trading Account", marginAccount: "Margin-Guthaben",
    unifiedHint: "Ein- und Auszahlungen", transferWord: "Transfer", maxWord: "Max",
    fromWord: "Von", toWord: "An", swapDir: "Richtung wechseln",
    confirmTransfer: "Transfer bestätigen", transferDone: "Transfer abgeschlossen",
    totalPnl: "Realisierter Gesamt-PnL", noClosedTrades: "Noch keine geschlossenen Trades.",
    sectionStub: ".", sectionWord: "Bereich",
    marginMode: "Margin-Modus", isolated: "Isoliert", chooseLev: "Hebel wählen",
    contractData: "Kontraktdaten", expiry: "Laufzeit", perpWord: "Unbefristet", priceWord: "Preis",
    minLev: "Min. Hebel", maxLev: "Max. Hebel", showWord: "Anzeigen", hideWord: "Ausblenden", notSet: "Nicht gesetzt",
    stFilled: "Ausgeführt", stPlaced: "Platziert", stCanceled: "Storniert",
    rManual: "Manuell", rOpen: "Eröffnung", rOpenLimit: "Eröffnung (Limit)", rClose: "Schließung",
    tPriceNotLoaded: "Preis noch nicht geladen, einen Moment",
    tEnterQty: "Menge vor der Orderplatzierung angeben", tBadPrice: "Ungültiger Preis",
    tNeedMargin: "Nicht genug verfügbares Guthaben (benötigt ≈", tTpBadPrice: "Gültigen Take-Profit-Preis angeben",
    tSlBadPrice: "Gültigen Stop-Loss-Preis angeben",
    tTpBuy: "TP für Long muss über dem Einstieg liegen", tTpSell: "TP für Short muss unter dem Einstieg liegen",
    tSlBuy: "SL für Long muss unter dem Einstieg liegen", tSlSell: "SL für Short muss über dem Einstieg liegen",
    tOrderCanceled: "Order storniert", tLongOpened: "Long eröffnet", tShortOpened: "Short eröffnet",
    tLimitPlacedBuy: "Kauf-Limit-Order platziert bei", tLimitPlacedSell: "Verkauf-Limit-Order platziert bei",
    tLimitFilled: "Limit-Order ausgeführt bei", tPosClosed: "Position geschlossen",
    tClosedBy: "Position geschlossen durch", tDrawingsCleared: "Alle Zeichnungen entfernt",
    tChartSettings: "Chart-Einstellungen", tUserPosClosed: "Benutzerposition geschlossen",
    csVolume: "Volumen anzeigen", csLogScale: "Logarithmische Skala", csFit: "An Bildschirm anpassen",
    tTpSlSet: "TP/SL der Position aktualisiert", saveWord: "Speichern", editTpSl: "TP/SL ändern",
    userHistory: "Benutzer-Historie", noUserHistory: "Benutzer haben noch keine Handelshistorie.",
    allUsersWord: "Alle", feeCol: "Gebühr",
    statTrades: "Trades gesamt", statWins: "Gewinne", statLosses: "Verluste",
    statGrossProfit: "Bruttogewinn", statGrossLoss: "Bruttoverlust", statNetPnl: "Netto-PnL",
    statAvgWin: "Ø Gewinn", statAvgLoss: "Ø Verlust", statProfitFactor: "Profitfaktor",
    statFees: "Gebühren gesamt",
    userTransactions: "Benutzer-Transaktionen", txHistory: "Transaktionsverlauf", noTransactions: "Noch keine Transaktionen.",
    assetsTitle: "Meine Assets", positionHistory: "Positionsverlauf",
    depositWord: "Einzahlen", withdrawWord: "Abheben", closeWord: "Schließen",
    depositAmount: "Einzahlungsbetrag", withdrawAmount: "Auszahlungsbetrag",
    cardDetails: "Kartendaten", cardNumber: "Kartennummer", cardExpiry: "Gültig bis", cardHolder: "Karteninhaber",
    payMethod: "Einzahlungsmethode", methodCard: "Karte", ibanDetails: "IBAN-Daten", ibanHolder: "Kontoinhaber", ibanInvalid: "Ungültige IBAN",
    methodCrypto: "Krypto", cryptoNetwork: "Netzwerk", cryptoAddress: "Wallet-Adresse", cryptoAddressPlaceholder: "Wallet-Adresse eingeben",
    cryptoDepositAddress: "An diese Adresse senden", cryptoDepositNote: "Senden Sie genau den angegebenen Betrag an die untenstehende Adresse. Nach dem Senden Antrag zur Bestätigung durch den Administrator einreichen.",
    cryptoWithdrawNote: "Geben Sie Ihre Wallet-Adresse ein. Die Auszahlung erfolgt nach Bestätigung durch den Administrator.",
    ibanFirstName: "Vorname", ibanLastName: "Nachname",
    ibanDepositTitle: "Überweisungsdaten", ibanDepositBank: "Bank",
    ibanDepositNote: "Überweisen Sie den Betrag auf das untenstehende Konto und reichen Sie dann den Antrag zur Bestätigung ein.",
    ibanNotConfigured: "Bankdaten sind noch nicht verfügbar. Bitte kontaktieren Sie den Support.",
    adminIbanTitle: "Einzahlungs-IBAN (für Nutzer sichtbar)", adminIbanSave: "Speichern",
    adminIbanSaved: "Einzahlungsdaten gespeichert", backdateLabel: "Anzeigedatum",
    confirmDeposit: "Einzahlen", confirmWithdraw: "Geld abheben",
    fundNote: "Anträge werden manuell vom Admin bearbeitet. Mittel werden nach Bestätigung gutgeschrieben.",
    amountCol: "Betrag", cardCol: "Details", actionCol: "Aktion",
    txPending: "In Bearbeitung", txDone: "Erfolgreich", txRejected: "Abgelehnt",
    approveWord: "Bestätigen", rejectWord: "Ablehnen", onlyPending: "Nur ausstehend",
    tDepositReq: "Einzahlungsantrag erstellt", tWithdrawReq: "Auszahlungsantrag erstellt",
    tTxApproved: "Transaktion bestätigt", tTxRejected: "Transaktion abgelehnt",
    tInsuffWithdraw: "Auszahlungsbetrag übersteigt verfügbares Guthaben",
    availToBorrow: "Verfügbar zum Leihen", loanRate: "Zinssatz", loanNote: "Kredite gegen Ihre Assets. Der Zinssatz kann sich ändern.",
    priceUnit: "Preis", tpSlUnitHint: "Eingabe umschalten: Preis / Prozent (ROI inkl. Hebel)",
    tGranted: "Gutgeschrieben:", tGrantedTo: "an Benutzer", tDrawHint: "— mit der Maus auf dem Chart zeichnen",
    viewAll: "Ansicht: alle", viewSells: "Ansicht: nur Verkäufe", viewBuys: "Ansicht: nur Käufe",
    insuffWarn1: "Nicht genug verfügbares Guthaben: benötigt ≈", insuffWarn2: "verfügbar",
    insuffWarn3: "Übertragen Sie Guthaben vom Unified Trading Account auf Ihr Margin-Guthaben.",
    insuffWarn4: "Zahlen Sie Guthaben auf Ihr Unified Trading Account ein und übertragen Sie es dann auf Ihr Margin-Guthaben.",
    adminUsersInfo1: "Benutzer auf dem Server insgesamt:", adminUsersInfo2: "Die Liste aktualisiert sich alle 5 Sekunden.",
    adminUsersError: "Benutzerliste konnte nicht geladen werden:",
    adminUsersHint: "Prüfen Sie, ob server.js läuft und die Seite über die ngrok-URL geöffnet ist.",
    adminUsersLoading: "Benutzerliste wird geladen…",
    adminPosInfo: "Offene Positionen aller Benutzer. Automatische Aktualisierung. Schließen ist für den Benutzer unsichtbar.",
    noUserPositions: "Derzeit hat niemand offene Positionen.",
    authFill: "Benutzername und Passwort ausfüllen",
    authFooter1: "Konten liegen auf einem gemeinsamen Server — Anmeldung von jedem Gerät.",
    authFooter2: "Der erste registrierte Benutzer wird automatisch Administrator.",
    toolCursor: "Cursor", toolTrend: "Trendlinie (2 Punkte)", toolRay: "Strahl",
    toolHline: "Horizontales Niveau (1 Klick)", toolRect: "Rechteck",
    toolPencil: "Stift (freies Zeichnen)", toolText: "Textbeschriftung",
    toolRuler: "Lineal (Δ Preis / % / Balken)", toolSearch: "Handelspaar suchen", toolTrash: "Alle Zeichnungen löschen",
    toolEraser: "Radierer (Objekt löschen)", toolColor: "Zeichenfarbe", tEraserHint: "Objekt antippen zum Löschen",
    dragW: "Ziehen zum Anpassen. Doppelklick zum Zurücksetzen",
    dragH: "Ziehen, um die Panelhöhe zu ändern. Doppelklick zum Zurücksetzen",
    dragB: "Ziehen, um das Orderbuch zu vergrößern. Doppelklick zum Zurücksetzen",
    pickPairTitle: "Handelspaar wählen",
    connecting: "Verbindung zum Server…", triggerPrice: "Auslösepreis", noData: "keine Daten",
    indexPriceShort: "Indexpreis",
    serverUnavailable: "Server nicht erreichbar. Stellen Sie sicher, dass server.js läuft.",
  },
};


// ============================================================================
// Собственный селектор инструмента (спот/фьючерсы/форекс/акции)
// ============================================================================
function SymbolPicker({ symbol, onSelect, open, onClose, t }) {
  const { pair, category, market } = parseSymbol(symbol);
  // режим = вкладка: "spot" | "linear" (крипта) | "forex" | "stock"
  const initialMode = market === "forex" ? "forex" : market === "stock" ? "stock" : category;
  const [mode, setMode] = useState(initialMode);
  const [query, setQuery] = useState("");
  const [customPair, setCustomPair] = useState("");
  const [allPairs, setAllPairs] = useState(null); // null = загружается
  const isCrypto = mode === "spot" || mode === "linear";

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setQuery("");
      setCustomPair("");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Списки: крипта грузится с Bybit, форекс/акции — статические подборки
  useEffect(() => {
    if (!open) return;
    if (mode === "forex") { setAllPairs(FOREX_LIST); return; }
    if (mode === "stock") { setAllPairs(STOCK_LIST); return; }
    let active = true;
    setAllPairs(PAIRS_CACHE[mode] || null);
    fetchAllPairs(mode).then((list) => {
      if (active) setAllPairs(list);
    });
    return () => {
      active = false;
    };
  }, [open, mode]);

  if (!open) return null;

  const source = allPairs || [];
  const q = query.trim().toLowerCase();
  const filtered = (q ? source.filter((p) => p.toLowerCase().includes(q)) : source).slice(0, 500);

  const pick = (base) => {
    if (mode === "forex") onSelect(`FX:${base}`);
    else if (mode === "stock") onSelect(`STOCK:${base}`);
    else onSelect(`BYBIT:${base}${mode === "linear" ? ".P" : ""}`);
    onClose();
  };

  const applyCustom = () => {
    const raw = customPair.trim().toUpperCase();
    if (!raw) return;
    if (mode === "forex") {
      // допускаем ввод "EURUSD" или "EUR/USD"
      let v = raw.replace(/[^A-Z/]/g, "");
      if (!v.includes("/") && v.length === 6) v = `${v.slice(0, 3)}/${v.slice(3)}`;
      if (v.includes("/")) pick(v);
      return;
    }
    if (mode === "stock") {
      const v = raw.replace(/[^A-Z.]/g, "");
      if (v) pick(v);
      return;
    }
    const clean = raw.replace(/[^A-Z0-9]/g, "");
    if (!clean) return;
    pick(clean.endsWith("USDT") ? clean : `${clean}USDT`);
  };

  const TABS = [
    { id: "spot", label: t("spot") },
    { id: "linear", label: t("futures") },
    { id: "forex", label: t("forexWord") },
    { id: "stock", label: t("stocksWord") },
  ];

  return (
    <div className="absolute top-12 left-4 z-[80] w-72 max-w-[calc(100vw-2rem)] bg-slate-900 border border-slate-700 rounded shadow-xl p-3">
      <div className="grid grid-cols-4 gap-1 mb-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setMode(tab.id)}
            className={`py-1 rounded text-[12px] ${mode === tab.id ? "bg-blue-800 text-white font-bold" : "bg-slate-800 text-slate-400"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("searchPair")}
        className="w-full bg-[#010208] border border-slate-700 rounded px-2 py-1 mb-2 outline-none focus:border-blue-800 text-slate-200"
      />
      <div className="max-h-52 overflow-y-auto flex flex-col">
        {allPairs === null && <div className="text-slate-600 text-center py-3">{t("loadingPairs")}</div>}
        {allPairs !== null && filtered.length === 0 && (
          <div className="text-slate-600 text-center py-3">{t("nothingFound")}</div>
        )}
        {filtered.map((p) => (
          <button
            key={p}
            onClick={() => pick(p)}
            className={`flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-slate-800 ${
              p === pair ? "text-blue-800 font-bold" : "text-slate-300"
            }`}
          >
            <CoinIcon pair={p} size={16} market={mode === "forex" ? "forex" : mode === "stock" ? "stock" : "crypto"} />
            {p}
            {mode === "linear" ? ".P" : ""}
          </button>
        ))}
      </div>
      <div className="border-t border-slate-800 mt-2 pt-2 flex gap-2">
        <input
          value={customPair}
          onChange={(e) => setCustomPair(e.target.value)}
          placeholder={mode === "forex" ? "EUR/USD" : mode === "stock" ? "AAPL" : t("customTicker")}
          className="flex-1 bg-[#010208] border border-slate-700 rounded px-2 py-1 outline-none focus:border-blue-800 text-slate-200"
        />
        <button onClick={applyCustom} className="bg-slate-800 hover:bg-slate-700 px-2 rounded text-slate-200">
          OK
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Основное приложение
// ============================================================================
// ============================================================================
// Вкладка "Обзор" — ключевые показатели текущего инструмента
// ============================================================================
function OverviewTab({ ticker, displaySymbol, isFutures, t }) {
  if (!ticker) {
    return <div className="p-6 text-slate-600 text-sm">{t("loadingInstrument")}</div>;
  }
  const rows = [
    [t("indexPrice"), fmt(ticker.indexPrice)],
    [t("lastPrice"), fmt(ticker.lastPrice)],
    [t("high24h"), fmt(ticker.high24h)],
    [t("low24h"), fmt(ticker.low24h)],
    [t("turnover24h") + " USDT", fmt2(ticker.turnover24h)],
    [t("change24h"), `${ticker.changePct >= 0 ? "+" : ""}${ticker.changePct.toFixed(2)}%`],
  ];
  if (isFutures) {
    rows.push([t("markPrice"), fmt(ticker.markPrice)]);
    rows.push([t("fundingRate"), `${ticker.fundingRate?.toFixed(4) ?? "—"}%`]);
  }
  return (
    <div className="p-5 max-w-md">
      <h3 className="text-slate-100 font-bold text-sm mb-3">
        {displaySymbol} — {isFutures ? t("perpContract") : t("spotPair")}
      </h3>
      <div className="flex flex-col gap-1.5">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between text-xs py-1 border-b border-slate-900">
            <span className="text-slate-500">{label}</span>
            <span className="text-slate-200">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Вкладка "Данные" — более подробная сырая выдача тикера (для тех, кому нужны все поля)
// ============================================================================
function DataTab({ ticker, price, t }) {
  if (!ticker) {
    return <div className="p-6 text-slate-600 text-sm">{t("loadingData")}</div>;
  }
  const entries = Object.entries(ticker).filter(([k]) => k !== "pair" && k !== "category");
  return (
    <div className="p-5">
      <table className="text-xs">
        <tbody>
          {entries.map(([key, value]) => (
            <tr key={key} className="border-b border-slate-900">
              <td className="pr-6 py-1 text-slate-500">{key}</td>
              <td className="py-1 text-slate-200">
                {value === null ? "—" : typeof value === "number" ? fmt(value) : String(value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Вкладка "Лента новостей" — реальные заголовки с публичного API CryptoCompare
// ============================================================================
function NewsFeedTab({ lang, t }) {
  const [news, setNews] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const apiLang = lang === "ru" ? "RU" : lang === "de" ? "DE" : "EN";
        // Ходим через свой сервер (server.js /api/market/news) — прямой запрос
        // на CryptoCompare из браузера у нас режется (CORS/география).
        const res = await fetch(`${API_URL}/api/market/news?lang=${apiLang}`, {
          headers: { "ngrok-skip-browser-warning": "true" },
        });
        const json = await res.json();
        if (!active) return;
        if (Array.isArray(json?.Data) && json.Data.length > 0) {
          setNews(json.Data.slice(0, 20));
          setError(false);
        } else {
          setError(true);
        }
      } catch (e) {
        if (active) setError(true);
      }
    }
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [lang]);

  if (error) {
    return <div className="p-6 text-slate-600 text-sm">{t("newsError")}</div>;
  }
  if (!news) {
    return <div className="p-6 text-slate-600 text-sm">{t("loadingNews")}</div>;
  }
  return (
    <div className="p-4 flex flex-col gap-2 max-w-2xl">
      {news.map((item) => (
        <a
          key={item.id}
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block border-b border-slate-900 pb-2 hover:bg-slate-900 rounded px-2 py-1 -mx-2"
        >
          <div className="text-slate-200 text-xs font-medium">{item.title}</div>
          <div className="text-slate-600 text-[11px] mt-0.5">
            {item.source_info?.name || item.source} · {new Date(item.published_on * 1000).toLocaleString()}
          </div>
        </a>
      ))}
    </div>
  );
}

// Встроенная панель TP/SL — раскрывается прямо в сайдбаре под кнопкой, без модала.
// Допускает знак минус в начале (для ввода отрицательного ROI, например у стоп-лосса)
function sanitizeSigned(raw) {
  const neg = String(raw).trim().startsWith("-");
  const body = sanitizeNumber(String(raw));
  return (neg ? "-" : "") + body;
}

function TpSlPanel({
  open,
  onClose,
  price,
  leverage,
  side,
  tpEnabled,
  slEnabled,
  tpPrice,
  slPrice,
  onChange,
  t,
}) {
  const priceNum = Number(price);
  const hasPrice = isFinite(priceNum) && priceNum > 0;
  const lev = Number(leverage) > 0 ? Number(leverage) : 1;
  // Направление сделки: для лонга прибыль при росте цены, для шорта — при падении.
  // dirMul переворачивает знак ROI для шорта, чтобы «+ %» всегда означало прибыль,
  // а «− %» — убыток, независимо от направления.
  const isShort = side === "sell";
  const dirMul = isShort ? -1 : 1;

  // ROI (%) со знаком относительно направления: + прибыль, − убыток (с учётом плеча).
  const roiFromPrice = (triggerPrice) => {
    const p = Number(triggerPrice);
    if (!isFinite(p) || p <= 0 || !hasPrice) return null;
    return ((p - priceNum) / priceNum) * 100 * lev * dirMul;
  };
  const priceFromRoi = (roi) => {
    const r = Number(roi);
    if (!isFinite(r) || !hasPrice) return "";
    const p = priceNum * (1 + (r * dirMul) / (100 * lev));
    if (!(p > 0)) return "";
    const digits = priceNum >= 100 ? 2 : priceNum >= 1 ? 4 : 6;
    return p.toFixed(digits);
  };

  // Локальный стейт для ROI-инпутов: обновляется только когда поле не в фокусе,
  // чтобы курсор не прыгал в конец при изменении цены.
  const [tpRoiStr, setTpRoiStr] = useState("");
  const [slRoiStr, setSlRoiStr] = useState("");
  const tpRoiFocused = useRef(false);
  const slRoiFocused = useRef(false);
  useEffect(() => {
    if (!tpRoiFocused.current) {
      const r = roiFromPrice(tpPrice);
      setTpRoiStr(r !== null ? r.toFixed(1) : "");
    }
  }, [tpPrice, price, side]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!slRoiFocused.current) {
      const r = roiFromPrice(slPrice);
      setSlRoiStr(r !== null ? (-r).toFixed(1) : "");
    }
  }, [slPrice, price, side]); // eslint-disable-line react-hooks/exhaustive-deps

  const tpRoi = tpEnabled ? roiFromPrice(tpPrice) : null;
  const slRoi = slEnabled ? roiFromPrice(slPrice) : null;

  if (!open) return null;

  const roiInputCls =
    "w-20 bg-[#010208] border border-slate-700 rounded px-2 py-1 text-sm text-right outline-none disabled:opacity-40";

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <span className="text-slate-100 font-semibold text-base">{t("tpSl")}</span>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-lg leading-none">✕</button>
        </div>

        {/* Переключатель Лонг / Шорт */}
        <div className="flex rounded overflow-hidden border border-slate-700 mb-3 text-xs font-semibold">
          <button
            onClick={() => onChange("side", "buy")}
            className={`flex-1 py-1.5 transition-colors ${!isShort ? "bg-green-600 text-white" : "bg-transparent text-slate-400 hover:text-slate-200"}`}
          >
            {t("buyLong")}
          </button>
          <button
            onClick={() => onChange("side", "sell")}
            className={`flex-1 py-1.5 transition-colors ${isShort ? "bg-red-600 text-white" : "bg-transparent text-slate-400 hover:text-slate-200"}`}
          >
            {t("sellShort")}
          </button>
        </div>

        {hasPrice && (
          <div className="flex justify-between text-[11px] text-slate-500 mb-3">
            <span>{t("triggerPrice")}: <span className="text-slate-300">{priceNum}</span></span>
            <span>{t("leverage")}: <span className="text-slate-300">{lev}x</span></span>
          </div>
        )}

        {/* Take Profit */}
        <div className="mb-4">
          <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={tpEnabled}
              onChange={(e) => onChange("tpEnabled", e.target.checked)}
              className="accent-green-500"
            />
            <span className="text-green-400 font-medium">{t("takeProfit")}</span>
          </label>
          <div className="flex gap-2">
            <input
              disabled={!tpEnabled}
              value={tpPrice}
              onChange={(e) => onChange("tpPrice", sanitizeNumber(e.target.value))}
              placeholder={t("triggerPrice")}
              className="flex-1 bg-[#010208] border border-slate-700 rounded px-2 py-1 text-sm text-slate-100 outline-none disabled:opacity-40 focus:border-green-700"
            />
            <div className="relative">
              <input
                disabled={!tpEnabled}
                value={tpRoiStr}
                onFocus={() => { tpRoiFocused.current = true; }}
                onBlur={() => { tpRoiFocused.current = false; const r = roiFromPrice(tpPrice); setTpRoiStr(r !== null ? r.toFixed(1) : ""); }}
                onChange={(e) => { setTpRoiStr(e.target.value); onChange("tpPrice", priceFromRoi(sanitizeSigned(e.target.value))); }}
                placeholder="ROI"
                className={`${roiInputCls} pr-5 ${Number(tpRoiStr) < 0 ? "text-red-400" : "text-green-400"} focus:border-green-700`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 text-xs pointer-events-none">%</span>
            </div>
          </div>
        </div>

        {/* Stop Loss */}
        <div className="mb-4">
          <label className="flex items-center gap-2 text-xs mb-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={slEnabled}
              onChange={(e) => onChange("slEnabled", e.target.checked)}
              className="accent-red-500"
            />
            <span className="text-red-400 font-medium">{t("stopLoss")}</span>
          </label>
          <div className="flex gap-2">
            <input
              disabled={!slEnabled}
              value={slPrice}
              onChange={(e) => onChange("slPrice", sanitizeNumber(e.target.value))}
              placeholder={t("triggerPrice")}
              className="flex-1 bg-[#010208] border border-slate-700 rounded px-2 py-1 text-sm text-slate-100 outline-none disabled:opacity-40 focus:border-red-700"
            />
            <div className="relative">
              <input
                disabled={!slEnabled}
                value={slRoiStr}
                onFocus={() => { slRoiFocused.current = true; }}
                onBlur={() => { slRoiFocused.current = false; const r = roiFromPrice(slPrice); setSlRoiStr(r !== null ? (-r).toFixed(1) : ""); }}
                onChange={(e) => { const v = sanitizeSigned(e.target.value); setSlRoiStr(v); const n = Number(v); onChange("slPrice", isFinite(n) ? priceFromRoi(-n) : ""); }}
                placeholder="ROI"
                className={`${roiInputCls} pr-5 text-red-400 focus:border-red-700`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 text-xs pointer-events-none">%</span>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 bg-[#1e40af] hover:bg-[#172554] text-white text-sm font-semibold py-2 rounded"
          >
            {t("confirm")}
          </button>
          <button
            onClick={() => {
              onChange("tpEnabled", false);
              onChange("slEnabled", false);
              onChange("tpPrice", "");
              onChange("slPrice", "");
              onClose();
            }}
            className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm py-2 rounded"
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Панель администратора: список пользователей, начисление средств, права админа
// ============================================================================
function UsersAdminPanel({ users, error, onGrant, onToggleAdmin, t }) {
  const [amounts, setAmounts] = useState({});

  // Диагностика вместо молчаливой пустоты: сразу видно, ЧТО именно не так
  if (error) {
    return (
      <div className="py-6 text-center">
        <div className="text-red-400 mb-1">{t("adminUsersError")} {error}</div>
        <div className="text-slate-600 text-[11px]">
          {t("adminUsersHint")}
        </div>
      </div>
    );
  }
  if (users.length === 0) {
    return <div className="py-6 text-center text-slate-600">{t("adminUsersLoading")}</div>;
  }

  return (
    <div className="py-2">
      <div className="text-slate-600 text-[11px] mb-1">
        {t("adminUsersInfo1")} {users.length}. {t("adminUsersInfo2")}
      </div>
      <table className="w-full text-left">
        <thead className="text-slate-500">
          <tr>
            <th className="font-normal">{t("accountId")}</th>
            <th className="font-normal">{t("userCol")}</th>
            <th className="font-normal">{t("emailWord")}</th>
            <th className="font-normal">{t("roleCol")}</th>
            <th className="font-normal">{t("balanceCol")}</th>
            <th className="font-normal">{t("distributeFunds")}</th>
            <th className="font-normal"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.username} className="border-t border-slate-900">
              <td className="py-1.5 text-slate-400 tabular-nums">{u.id || "—"}</td>
              <td className="py-1.5 text-slate-200">{u.username}</td>
              <td className="text-slate-400 break-all">{u.email || "—"}</td>
              <td>
                {u.isAdmin ? <span className="text-blue-800 font-bold">{t("admin")}</span> : <span className="text-slate-500">—</span>}
              </td>
              <td className="text-slate-200">
                {(() => {
                  const total = (u.balance || 0) + (u.marginBalance || 0);
                  return (
                    <>
                      <div>{fmt2(total)} USDT <span className="text-slate-500 text-xs">/ {fmtEur(total)} EUR</span></div>
                      <div className="text-slate-500 text-[10px]">
                        {t("unifiedAccount")}: {fmt2(u.balance || 0)} · {t("marginAccount")}: {fmt2(u.marginBalance || 0)}
                      </div>
                    </>
                  );
                })()}
              </td>
              <td>
                <div className="flex items-center gap-1">
                  <input
                    value={amounts[u.username] ?? ""}
                    onChange={(e) =>
                      setAmounts((a) => ({ ...a, [u.username]: sanitizeNumber(e.target.value) }))
                    }
                    placeholder="0.00"
                    className="w-20 bg-[#010208] border border-slate-700 rounded px-2 py-1 outline-none focus:border-blue-800"
                  />
                  <button
                    onClick={() => {
                      const amt = Number(amounts[u.username]);
                      if (amt > 0) {
                        onGrant(-amt, u.username);
                        setAmounts((a) => ({ ...a, [u.username]: "" }));
                      }
                    }}
                    className="bg-red-700 hover:bg-red-600 text-white font-bold px-2 py-1 rounded"
                  >
                    −
                  </button>
                  <button
                    onClick={() => {
                      const amt = Number(amounts[u.username]);
                      if (amt > 0) {
                        onGrant(amt, u.username);
                        setAmounts((a) => ({ ...a, [u.username]: "" }));
                      }
                    }}
                    className="bg-blue-800 hover:bg-blue-800 text-white font-bold px-2 py-1 rounded"
                  >
                    +
                  </button>
                </div>
              </td>
              <td>
                <button
                  onClick={() => onToggleAdmin(u.username)}
                  className="text-slate-500 hover:text-blue-800"
                >
                  {u.isAdmin ? t("revokeAdmin") : t("grantAdmin")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Панель администратора: открытые позиции ВСЕХ пользователей + тихое закрытие
// + управление TP/SL позиций любого пользователя
// ============================================================================
function AdminPosRow({ p, pl, onClose, onSetTpSl, t }) {
  const [tp, setTp] = useState(p.tp == null ? "" : String(p.tp));
  const [sl, setSl] = useState(p.sl == null ? "" : String(p.sl));
  const [unit, setUnit] = useState("price"); // "price" | "pct"
  // синхронизируем локальные поля, если значения пришли извне (например, снимок обновился)
  useEffect(() => { if (unit === "price") setTp(p.tp == null ? "" : String(p.tp)); }, [p.tp, unit]);
  useEffect(() => { if (unit === "price") setSl(p.sl == null ? "" : String(p.sl)); }, [p.sl, unit]);
  const inputCls =
    "w-20 bg-slate-900 border border-slate-800 rounded px-1 py-0.5 text-slate-200 focus:outline-none focus:border-blue-800";

  // ROI% (с учётом плеча) -> абсолютная цена срабатывания.
  // TP: положительный % = прибыль. SL: положительный % = убыток такого размера.
  const pctToPrice = (pctStr, kind) => {
    if (pctStr === "" || pctStr == null) return "";
    const pct = Number(pctStr);
    if (!isFinite(pct)) return "";
    const move = pct / 100 / (p.leverage || 1); // относительное изменение цены
    const profitUp = p.dir === "buy"; // для лонга прибыль при росте цены
    let signedMove;
    if (kind === "tp") signedMove = profitUp ? move : -move;
    else signedMove = profitUp ? -move : move; // sl — убыток
    return +(p.entry * (1 + signedMove)).toFixed(8);
  };

  const submit = () => {
    if (unit === "pct") {
      onSetTpSl(p.username, p.id, pctToPrice(tp, "tp"), pctToPrice(sl, "sl"));
    } else {
      onSetTpSl(p.username, p.id, tp, sl);
    }
  };

  const toggleUnit = () => {
    setUnit((u) => {
      const next = u === "price" ? "pct" : "price";
      // при переключении на цену — подставим текущие значения позиции; на % — очистим
      if (next === "price") {
        setTp(p.tp == null ? "" : String(p.tp));
        setSl(p.sl == null ? "" : String(p.sl));
      } else {
        setTp("");
        setSl("");
      }
      return next;
    });
  };

  return (
    <tr className="border-t border-slate-900">
      <td className="py-1.5 text-slate-200">{p.username}</td>
      <td className="text-slate-300">{p.symbol.includes(":") ? p.symbol.split(":")[1] : p.symbol}</td>
      <td className={p.dir === "buy" ? "text-green-500" : "text-red-500"}>
        {p.dir === "buy" ? t("long") : t("short")}
      </td>
      <td>{fmtQty(p.qty)}</td>
      <td>{fmt(p.entry)}</td>
      <td>{p.leverage}x</td>
      <td className={pl === null ? "text-slate-500" : pl >= 0 ? "text-green-500" : "text-red-500"}>
        {pl === null ? "—" : `${pl >= 0 ? "+" : ""}${fmt2(pl)}`}
      </td>
      <td>
        <div className="flex items-center gap-0.5">
          <input value={tp} onChange={(e) => setTp(e.target.value)} inputMode="decimal" placeholder={unit === "pct" ? "%" : "TP"} className={inputCls} />
          {unit === "pct" && <span className="text-slate-600 text-[11px]">%</span>}
        </div>
      </td>
      <td>
        <div className="flex items-center gap-0.5">
          <input value={sl} onChange={(e) => setSl(e.target.value)} inputMode="decimal" placeholder={unit === "pct" ? "%" : "SL"} className={inputCls} />
          {unit === "pct" && <span className="text-slate-600 text-[11px]">%</span>}
        </div>
      </td>
      <td className="whitespace-nowrap">
        <button
          onClick={toggleUnit}
          title={t("tpSlUnitHint")}
          className="text-slate-500 hover:text-blue-800 mr-3 border border-slate-800 rounded px-1"
        >
          {unit === "pct" ? "%" : t("priceUnit")}
        </button>
        <button
          onClick={submit}
          className="text-blue-800 hover:text-blue-800 mr-3"
        >
          {t("saveWord")}
        </button>
        <button onClick={() => onClose(p.username, p.id)} className="text-slate-500 hover:text-red-400">
          {t("close")}
        </button>
      </td>
    </tr>
  );
}

function AdminPositionsPanel({ accounts, priceOf, onClose, onSetTpSl, t }) {
  const rows = accounts.flatMap((a) => a.positions.map((p) => ({ ...p, username: a.username })));
  if (rows.length === 0) {
    return <div className="py-6 text-center text-slate-600">{t("noUserPositions")}</div>;
  }
  const pnlOf = (p) => {
    const price = priceOf(p.symbol);
    if (price === null || price === undefined) return null;
    const diff = p.dir === "buy" ? price - p.entry : p.entry - price;
    // qty уже с плечом — второй раз на плечо не умножаем.
    return diff * p.qty;
  };
  return (
    <div className="py-2">
      <div className="text-slate-600 text-[11px] mb-1">
        {t("adminPosInfo")}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-left min-w-[720px]">
        <thead className="text-slate-500">
          <tr>
            <th className="font-normal">{t("userCol")}</th>
            <th className="font-normal">{t("pairCol")}</th>
            <th className="font-normal">{t("sideCol")}</th>
            <th className="font-normal">{t("qtyCol")}</th>
            <th className="font-normal">{t("entryCol")}</th>
            <th className="font-normal">{t("levCol")}</th>
            <th className="font-normal">PnL</th>
            <th className="font-normal">TP</th>
            <th className="font-normal">SL</th>
            <th className="font-normal"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <AdminPosRow
              key={p.username + p.id}
              p={p}
              pl={pnlOf(p)}
              onClose={onClose}
              onSetTpSl={onSetTpSl}
              t={t}
            />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

// ============================================================================
// Экран входа / регистрации
// ============================================================================
// Статистика по закрытым сделкам: винрейт, суммы прибыли/убытка, комиссии и т.д.
function calcTradeStats(closed) {
  const wins = closed.filter((h) => h.pnl > 0);
  const losses = closed.filter((h) => h.pnl < 0);
  const grossProfit = wins.reduce((s, h) => s + h.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((s, h) => s + h.pnl, 0));
  const totalPnl = grossProfit - grossLoss;
  const totalFees = closed.reduce((s, h) => s + (h.fee || 0), 0);
  const n = closed.length;
  return {
    n,
    wins: wins.length,
    losses: losses.length,
    winRate: n ? (wins.length / n) * 100 : 0,
    lossRate: n ? (losses.length / n) * 100 : 0,
    grossProfit,
    grossLoss,
    totalPnl,
    totalFees,
    profitPct: grossProfit + grossLoss > 0 ? (grossProfit / (grossProfit + grossLoss)) * 100 : 0,
    lossPct: grossProfit + grossLoss > 0 ? (grossLoss / (grossProfit + grossLoss)) * 100 : 0,
    avgWin: wins.length ? grossProfit / wins.length : 0,
    avgLoss: losses.length ? grossLoss / losses.length : 0,
    profitFactor: grossLoss > 0 ? grossProfit / grossLoss : grossProfit > 0 ? Infinity : 0,
  };
}

// Блок карточек статистики (используется и у пользователя, и в админке)
function TradeStatsCards({ stats, t }) {
  const s = stats;
  const pf = s.profitFactor === Infinity ? "∞" : s.profitFactor.toFixed(2);
  const cards = [
    { label: t("statTrades"), value: s.n, color: "text-slate-50" },
    { label: t("statWins"), value: `${s.wins} (${s.winRate.toFixed(1)}%)`, color: "text-green-500" },
    { label: t("statLosses"), value: `${s.losses} (${s.lossRate.toFixed(1)}%)`, color: "text-red-500" },
    { label: t("statGrossProfit"), value: `+${fmt2(s.grossProfit)} (${s.profitPct.toFixed(1)}%)`, color: "text-green-500" },
    { label: t("statGrossLoss"), value: `-${fmt2(s.grossLoss)} (${s.lossPct.toFixed(1)}%)`, color: "text-red-500" },
    { label: t("statNetPnl"), value: `${s.totalPnl >= 0 ? "+" : ""}${fmt2(s.totalPnl)}`, color: s.totalPnl >= 0 ? "text-green-500" : "text-red-500" },
    { label: t("statAvgWin"), value: `+${fmt2(s.avgWin)}`, color: "text-green-500" },
    { label: t("statAvgLoss"), value: `-${fmt2(s.avgLoss)}`, color: "text-red-500" },
    { label: t("statProfitFactor"), value: pf, color: "text-slate-50" },
    { label: t("statFees"), value: fmt2(s.totalFees), color: "text-blue-800" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
      {cards.map((c) => (
        <div key={c.label} className="bg-slate-900 border border-slate-800 rounded p-2">
          <div className="text-slate-500 text-[11px]">{c.label}</div>
          <div className={`font-bold ${c.color}`}>{c.value}</div>
        </div>
      ))}
    </div>
  );
}

// Админ: история торговли всех пользователей + статистика по каждому
function AdminHistoryPanel({ accounts, t }) {
  const [selected, setSelected] = useState(null); // null = все
  const names = accounts.map((a) => a.username);
  const shown = selected ? accounts.filter((a) => a.username === selected) : accounts;
  const allRows = shown.flatMap((a) => (a.history || []).map((h) => ({ ...h, username: a.username })));
  allRows.sort((a, b) => b.time - a.time);
  const closed = allRows.filter((h) => h.pnl !== null && h.pnl !== undefined);
  const stats = calcTradeStats(closed);

  if (accounts.length === 0) {
    return <div className="text-center text-slate-700 py-6">{t("noUserHistory")}</div>;
  }
  return (
    <div className="py-2">
      {/* фильтр по пользователю */}
      <div className="flex gap-1 flex-wrap mb-3">
        <button
          onClick={() => setSelected(null)}
          className={`px-2 py-1 rounded text-xs ${selected === null ? "bg-blue-800 text-white font-bold" : "bg-slate-800 text-slate-400 hover:text-slate-200"}`}
        >
          {t("allUsersWord")}
        </button>
        {names.map((n) => (
          <button
            key={n}
            onClick={() => setSelected(n)}
            className={`px-2 py-1 rounded text-xs ${selected === n ? "bg-blue-800 text-white font-bold" : "bg-slate-800 text-slate-400 hover:text-slate-200"}`}
          >
            {n}
          </button>
        ))}
      </div>

      {/* статистика */}
      {closed.length > 0 ? (
        <TradeStatsCards stats={stats} t={t} />
      ) : (
        <div className="text-slate-600 text-xs mb-3">{t("noClosedTrades")}</div>
      )}

      {/* таблица истории */}
      <div className="overflow-x-auto">
        <table className="w-full text-left min-w-[760px]">
          <thead className="text-slate-500">
            <tr>
              <th className="font-normal">{t("timeCol")}</th>
              <th className="font-normal">{t("userCol")}</th>
              <th className="font-normal">{t("pairCol")}</th>
              <th className="font-normal">{t("sideCol")}</th>
              <th className="font-normal">{t("qtyCol")}</th>
              <th className="font-normal">{t("entryCol")}</th>
              <th className="font-normal">{t("exitCol")}</th>
              <th className="font-normal">PnL</th>
              <th className="font-normal">{t("feeCol")}</th>
              <th className="font-normal">{t("reasonCol")}</th>
            </tr>
          </thead>
          <tbody>
            {allRows.slice(0, 200).map((h) => (
              <tr key={`${h.username}-${h.id}`} className="border-t border-slate-900">
                <td className="py-1 whitespace-nowrap">{new Date(h.time).toLocaleString()}</td>
                <td className="text-slate-200">{h.username}</td>
                <td className="text-slate-300">{h.symbol?.includes(":") ? h.symbol.split(":")[1] : h.symbol ?? "—"}</td>
                <td className={h.dir === "buy" ? "text-green-500" : "text-red-500"}>
                  {h.dir === "buy" ? t("long") : t("short")}
                </td>
                <td>{h.qty}</td>
                <td>{fmt(h.entry)}</td>
                <td>{h.exit ? fmt(h.exit) : "—"}</td>
                <td className={h.pnl == null ? "text-slate-500" : h.pnl >= 0 ? "text-green-500" : "text-red-500"}>
                  {h.pnl == null ? "—" : `${h.pnl >= 0 ? "+" : ""}${fmt2(h.pnl)}`}
                </td>
                <td className="text-blue-800">{h.fee ? fmt2(h.fee) : "—"}</td>
                <td className="text-slate-400">{t(h.reason)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Значок статуса транзакции (pending / done / rejected)
function TxStatusBadge({ status, t }) {
  const map = {
    pending: { text: t("txPending"), cls: "bg-yellow-500/15 text-yellow-500 border-yellow-500/30" },
    done: { text: t("txDone"), cls: "bg-green-500/15 text-green-500 border-green-500/30" },
    rejected: { text: t("txRejected"), cls: "bg-red-500/15 text-red-500 border-red-500/30" },
  };
  const s = map[status] || map.pending;
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] border ${s.cls}`}>{s.text}</span>;
}

// Полноэкранное окно пополнения / вывода средств
function FundModal({ mode, balance, token, onClose, onSubmit, t }) {
  const isDeposit = mode === "deposit";
  const [method, setMethod] = useState("card"); // "card" | "iban" | "crypto"
  const [amount, setAmount] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState("");
  // Имя/фамилия владельца IBAN (для вывода — куда уходят деньги)
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);
  // Реквизиты для пополнения по IBAN задаёт админ. Здесь показываем как есть.
  const [depositIban, setDepositIban] = useState(null); // null=загрузка, {}=нет данных
  const [ibanCopied, setIbanCopied] = useState(false);
  useEffect(() => {
    if (!isDeposit) return;
    let alive = true;
    apiFetch("/api/settings/deposit-iban", { token })
      .then((r) => { if (alive) setDepositIban(r || {}); })
      .catch(() => { if (alive) setDepositIban({}); });
    return () => { alive = false; };
  }, [isDeposit, token]);
  const copyDepositIban = async () => {
    try {
      await navigator.clipboard.writeText((depositIban && depositIban.iban) || "");
      setIbanCopied(true);
      setTimeout(() => setIbanCopied(false), 1500);
    } catch (e) { /* clipboard может быть недоступен */ }
  };
  // Крипто: сеть и адрес. Адреса депозита фиксированные (демонстрационные).
  const CRYPTO_NETWORKS = [
    { id: "TRC20", label: "USDT (TRC20)", addr: "TXk8rQSAvPvBBNtqSoY6nCfsXWCSSpTVQC" },
    { id: "ERC20", label: "USDT (ERC20)", addr: "0x8f9a4b2c1d3e5f7a9b0c2d4e6f8a1b3c5d7e9f0a" },
    { id: "BEP20", label: "USDT (BEP20)", addr: "0x2d4e6f8a1b3c5d7e9f0a2b4c6d8e0f1a3b5c7d9e" },
    { id: "SOL",   label: "USDT (Solana)", addr: "7xKXtg2CW3jVzk8QpN4rBmYdZ5eF6hL9nP1qR3sT4uVw" },
  ];
  const [cryptoNet, setCryptoNet] = useState(CRYPTO_NETWORKS[0].id);
  const [cryptoAddr, setCryptoAddr] = useState("");
  const [copied, setCopied] = useState(false);
  const depositAddr = (CRYPTO_NETWORKS.find((n) => n.id === cryptoNet) || CRYPTO_NETWORKS[0]).addr;
  const copyAddr = async () => {
    try {
      await navigator.clipboard.writeText(depositAddr);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* игнорируем — clipboard может быть недоступен */ }
  };

  const formatCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  const formatExpiry = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };
  // IBAN: только буквы/цифры, верхний регистр, группы по 4 для читаемости
  const formatIban = (v) =>
    v.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 34).replace(/(.{4})/g, "$1 ").trim();

  const amt = Number(amount);
  const cardDigits = cardNumber.replace(/\D/g, "");
  const ibanDigits = iban.replace(/\s/g, "");
  // IBAN: начинается с 2 букв страны + 2 цифры контроля, длина 15–34 символа
  const ibanOk = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(ibanDigits);
  const depositIbanReady = !!(depositIban && depositIban.iban);
  const nameOk = firstName.trim().length > 0 && lastName.trim().length > 0;
  const requisitesOk =
    method === "crypto"
      ? (isDeposit ? amt > 0 : cryptoAddr.trim().length > 10)
      : method === "iban"
      ? (isDeposit ? depositIbanReady : ibanOk && nameOk)
      : cardDigits.length === 16 && expiry.length === 5 && cvv.length >= 3;
  const valid =
    amt > 0 &&
    requisitesOk &&
    (method === "card" ? holder.trim().length > 0 : true) &&
    (isDeposit || amt <= balance + 0.0000001);

  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    // holder: для карты — введённое имя; для IBAN-вывода — ФИО владельца;
    // для крипты — сеть (как и раньше).
    let holderOut = holder.trim();
    if (method === "crypto") holderOut = cryptoNet;
    else if (method === "iban" && !isDeposit) holderOut = `${firstName.trim()} ${lastName.trim()}`.trim();
    await onSubmit({
      kind: mode,
      amount: amt,
      method,
      cardNumber,
      iban: ibanDigits,
      holder: holderOut,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      cryptoNetwork: cryptoNet,
      cryptoAddress: isDeposit ? depositAddr : cryptoAddr,
    });
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[110] bg-[#010208] overflow-y-auto text-slate-200">
      {/* шапка */}
      <div className="sticky top-0 bg-[#010208] border-b border-slate-800 flex items-center justify-between px-4 sm:px-8 h-14">
        <span className="font-bold text-lg">{isDeposit ? t("depositWord") : t("withdrawWord")}</span>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-100 text-xl" title={t("closeWord")}>
          <i className="pi pi-times"></i>
        </button>
      </div>

      <div className="max-w-xl mx-auto px-4 sm:px-8 py-8">
        {/* доступный баланс */}
        <div className="bg-slate-900 rounded-xl p-4 mb-6 flex items-center justify-between">
          <span className="text-slate-500 text-sm">{isDeposit ? t("balanceWord") : t("availWord")}</span>
          <span className="text-slate-50 font-bold text-lg tabular-nums">{fmt2(balance)} USDT <span className="text-slate-500 text-sm">/ {fmtEur(balance)} EUR</span></span>
        </div>

        {/* сумма */}
        <label className="block text-slate-400 text-sm mb-1.5">{isDeposit ? t("depositAmount") : t("withdrawAmount")}</label>
        <div className="relative mb-5">
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="0.00"
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 pr-16 outline-none focus:border-[#1e40af] text-slate-100 text-lg"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">USDT</span>
        </div>
        {/* быстрый выбор суммы */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {(isDeposit ? [50, 100, 500, 1000] : [25, 50, 75, 100]).map((v) => (
            <button
              key={v}
              onClick={() => setAmount(isDeposit ? String(v) : (balance * (v / 100)).toFixed(2))}
              className="flex-1 min-w-[64px] bg-slate-900 border border-slate-700 rounded-lg py-2 text-sm hover:border-[#1e40af] hover:text-[#1e40af]"
            >
              {isDeposit ? `${v}` : `${v}%`}
            </button>
          ))}
        </div>

        {/* способ: карта / IBAN / Crypto */}
        <div className="text-slate-400 text-sm mb-3 font-semibold">{t("payMethod")}</div>
        <div className="grid grid-cols-3 gap-2 mb-5">
          {[
            { id: "card", icon: "pi-credit-card", label: t("methodCard") },
            { id: "iban", icon: "pi-building", label: "IBAN" },
            { id: "crypto", icon: "pi-bitcoin", label: t("methodCrypto") },
          ].map(({ id, icon, label }) => (
            <button
              key={id}
              onClick={() => setMethod(id)}
              className={`py-2.5 rounded-lg border font-semibold text-sm transition-colors ${
                method === id
                  ? "border-[#1e40af] text-[#1e40af] bg-[#1e40af]/10"
                  : "border-slate-700 text-slate-400 hover:border-slate-500"
              }`}
            >
              <i className={`pi ${icon} mr-2`}></i>{label}
            </button>
          ))}
        </div>

        {method === "crypto" ? (
          <>
            {/* выбор сети */}
            <div className="text-slate-400 text-sm mb-3 font-semibold">{t("cryptoNetwork")}</div>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {CRYPTO_NETWORKS.map((n) => (
                <button
                  key={n.id}
                  onClick={() => setCryptoNet(n.id)}
                  className={`py-2 rounded-lg border text-sm transition-colors ${
                    cryptoNet === n.id
                      ? "border-[#1e40af] text-[#1e40af] bg-[#1e40af]/10 font-semibold"
                      : "border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>

            {isDeposit ? (
              <>
                {/* Депозит: адрес */}
                <div className="text-slate-400 text-sm mb-3 font-semibold">{t("cryptoDepositAddress")}</div>
                <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-3">
                  {/* Адрес */}
                  <div className="text-slate-100 text-xs font-mono break-all mb-2">{depositAddr}</div>
                  <button
                    onClick={copyAddr}
                    className="text-xs px-3 py-1 rounded bg-slate-800 border border-slate-700 hover:border-[#1e40af] hover:text-[#1e40af]"
                  >
                    <i className="pi pi-copy mr-1"></i>{copied ? "✓" : "Copy"}
                  </button>
                </div>
                {/* Сумма и предупреждение */}
                {amt > 0 && (
                  <div className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 mb-3 flex justify-between text-sm">
                    <span className="text-slate-400">Amount to send</span>
                    <span className="text-slate-100 font-bold">{fmt2(amt)} USDT</span>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Вывод: пользователь вводит свой адрес */}
                <label className="block text-slate-500 text-xs mb-1.5">{t("cryptoAddress")}</label>
                <input
                  value={cryptoAddr}
                  onChange={(e) => setCryptoAddr(e.target.value.trim())}
                  placeholder={t("cryptoAddressPlaceholder")}
                  className="w-full mb-6 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100 font-mono text-sm"
                />
              </>
            )}
          </>
        ) : method === "card" ? (
          <>
            {/* данные карты */}
            <div className="text-slate-400 text-sm mb-3 font-semibold">{t("cardDetails")}</div>
            <label className="block text-slate-500 text-xs mb-1.5">{t("cardNumber")}</label>
            <input
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCard(e.target.value))}
              placeholder="0000 0000 0000 0000"
              inputMode="numeric"
              className="w-full mb-4 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100 tracking-widest"
            />
            <div className="flex gap-4 mb-4">
              <div className="flex-1">
                <label className="block text-slate-500 text-xs mb-1.5">{t("cardExpiry")}</label>
                <input
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  placeholder="MM/YY"
                  inputMode="numeric"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100"
                />
              </div>
              <div className="flex-1">
                <label className="block text-slate-500 text-xs mb-1.5">CVV</label>
                <input
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="123"
                  inputMode="numeric"
                  type="password"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            {isDeposit ? (
              <>
                {/* Пополнение по IBAN: реквизиты задаёт админ, пользователь только переводит */}
                <div className="text-slate-400 text-sm mb-3 font-semibold">{t("ibanDepositTitle")}</div>
                {depositIban === null ? (
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-6 text-slate-500 text-sm">…</div>
                ) : !depositIbanReady ? (
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-6 text-amber-400 text-sm">{t("ibanNotConfigured")}</div>
                ) : (
                  <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-4 space-y-3">
                    <p className="text-slate-500 text-xs">{t("ibanDepositNote")}</p>
                    <div>
                      <div className="text-slate-500 text-[11px] uppercase tracking-wide mb-0.5">{t("ibanHolder")}</div>
                      <div className="text-slate-100 font-semibold">{depositIban.holder || "—"}</div>
                    </div>
                    {depositIban.bank ? (
                      <div>
                        <div className="text-slate-500 text-[11px] uppercase tracking-wide mb-0.5">{t("ibanDepositBank")}</div>
                        <div className="text-slate-100">{depositIban.bank}</div>
                      </div>
                    ) : null}
                    <div>
                      <div className="text-slate-500 text-[11px] uppercase tracking-wide mb-0.5">IBAN</div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-100 font-mono tracking-wider break-all">{formatIban(depositIban.iban)}</span>
                        <button
                          onClick={copyDepositIban}
                          className="shrink-0 text-xs px-2.5 py-1 rounded bg-slate-800 border border-slate-700 hover:border-[#1e40af] hover:text-[#1e40af]"
                        >
                          <i className="pi pi-copy mr-1"></i>{ibanCopied ? "✓" : "Copy"}
                        </button>
                      </div>
                    </div>
                    {amt > 0 && (
                      <div className="flex justify-between border-t border-slate-800 pt-2 text-sm">
                        <span className="text-slate-400">Amount</span>
                        <span className="text-slate-100 font-bold">{fmt2(amt)} USDT / {fmtEur(amt)} EUR</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Вывод на IBAN: пользователь вводит свои реквизиты + ФИО владельца */}
                <div className="text-slate-400 text-sm mb-3 font-semibold">{t("ibanDetails")}</div>
                <label className="block text-slate-500 text-xs mb-1.5">IBAN</label>
                <input
                  value={iban}
                  onChange={(e) => setIban(formatIban(e.target.value))}
                  placeholder="DE89 3704 0044 0532 0130 00"
                  className="w-full mb-1.5 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100 tracking-wider"
                />
                {ibanDigits.length > 0 && !ibanOk && (
                  <div className="text-red-400 text-xs mb-2.5">{t("ibanInvalid")}</div>
                )}
                <div className="flex gap-4 mt-4 mb-2">
                  <div className="flex-1">
                    <label className="block text-slate-500 text-xs mb-1.5">{t("ibanFirstName")}</label>
                    <input
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value.toUpperCase())}
                      placeholder="IVAN"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-slate-500 text-xs mb-1.5">{t("ibanLastName")}</label>
                    <input
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value.toUpperCase())}
                      placeholder="IVANOV"
                      className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100"
                    />
                  </div>
                </div>
                <div className="mb-2.5"></div>
              </>
            )}
          </>
        )}

        {method === "card" && (
        <><label className="block text-slate-500 text-xs mb-1.5">{t("cardHolder")}</label>
        <input
          value={holder}
          onChange={(e) => setHolder(e.target.value.toUpperCase())}
          placeholder="IVAN IVANOV"
          className="w-full mb-6 bg-slate-900 border border-slate-700 rounded-lg px-4 py-3 outline-none focus:border-[#1e40af] text-slate-100"
        />
        </>)}

        {!isDeposit && amt > balance && (
          <div className="text-red-400 text-xs mb-4">{t("tInsuffWithdraw")}</div>
        )}

        <button
          onClick={submit}
          disabled={!valid || busy}
          className="w-full bg-[#1e40af] hover:bg-[#1e40af] text-white font-bold py-3.5 rounded-lg disabled:opacity-40 transition-colors"
        >
          {busy ? "…" : isDeposit ? t("confirmDeposit") : t("confirmWithdraw")}
        </button>
      </div>
    </div>
  );
}

// Таблица истории транзакций (используется у пользователя и в админке)
// Ячейка действий админа: кнопки принять/отклонить + выбор даты (задним числом).
// Дата задаётся только для пополнений — при подтверждении в истории покажется
// выбранный день вместо реального. Аудит на сервере сохраняется отдельно.
function TxActionCell({ tx, onDecide, t }) {
  const [date, setDate] = useState(""); // значение <input type=datetime-local>
  const backdate = date ? new Date(date).getTime() : undefined;
  const canBackdate = tx.kind === "deposit";
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1">
        <button
          onClick={() => onDecide(tx.username, tx.id, "approve", canBackdate ? backdate : undefined)}
          className="px-2 py-1 rounded bg-green-500/15 text-green-500 border border-green-500/30 hover:bg-green-500/25 text-xs"
        >
          {t("approveWord")}
        </button>
        <button
          onClick={() => onDecide(tx.username, tx.id, "reject")}
          className="px-2 py-1 rounded bg-red-500/15 text-red-500 border border-red-500/30 hover:bg-red-500/25 text-xs"
        >
          {t("rejectWord")}
        </button>
      </div>
      {canBackdate && (
        <label className="flex items-center gap-1 text-[11px] text-slate-500">
          <span className="whitespace-nowrap">{t("backdateLabel")}</span>
          <input
            type="datetime-local"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-1.5 py-0.5 text-slate-200 text-[11px] outline-none focus:border-[#1e40af]"
          />
          {date && (
            <button
              onClick={() => setDate("")}
              className="text-slate-600 hover:text-slate-300"
              title={t("closeWord")}
            >
              <i className="pi pi-times text-[10px]"></i>
            </button>
          )}
        </label>
      )}
    </div>
  );
}

function TransactionTable({ rows, showUser, t, onDecide }) {
  if (rows.length === 0) {
    return <div className="text-center text-slate-700 py-6">{t("noTransactions")}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left min-w-[640px]">
        <thead className="text-slate-500">
          <tr>
            <th className="font-normal">{t("timeCol")}</th>
            {showUser && <th className="font-normal">{t("userCol")}</th>}
            <th className="font-normal">{t("typeCol")}</th>
            <th className="font-normal">{t("amountCol")}</th>
            <th className="font-normal">{t("cardCol")}</th>
            <th className="font-normal">{t("statusCol")}</th>
            {onDecide && <th className="font-normal">{t("actionCol")}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((tx) => (
            <tr key={`${tx.username || ""}-${tx.id}`} className="border-t border-slate-900">
              <td className="py-2 whitespace-nowrap">{new Date(tx.time).toLocaleString()}</td>
              {showUser && <td className="text-slate-200">{tx.username}</td>}
              <td className={tx.kind === "deposit" ? "text-green-500" : "text-red-500"}>
                {tx.kind === "deposit" ? t("depositWord") : t("withdrawWord")}
              </td>
              <td className="tabular-nums">
                {tx.kind === "deposit" ? "+" : "-"}
                {fmt2(tx.amount)} USDT
              </td>
              <td className="text-slate-400">
                {tx.card || "—"}
                {tx.holder ? <div className="text-slate-600 text-xs">{tx.holder}</div> : null}
              </td>
              <td><TxStatusBadge status={tx.status} t={t} /></td>
              {onDecide && (
                <td>
                  {tx.status === "pending" ? (
                    <TxActionCell tx={tx} onDecide={onDecide} t={t} />
                  ) : (
                    <span className="text-slate-600 text-xs">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Админ: редактор реквизитов (фальшивого) счёта, который видят пользователи
// при пополнении по IBAN. Меняет только админ; сервер проверяет права.
function AdminDepositIbanEditor({ token, t }) {
  const [iban, setIban] = useState("");
  const [holder, setHolder] = useState("");
  const [bank, setBank] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const formatIban = (v) =>
    v.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 34).replace(/(.{4})/g, "$1 ").trim();
  useEffect(() => {
    let alive = true;
    apiFetch("/api/settings/deposit-iban", { token })
      .then((r) => {
        if (!alive || !r) return;
        setIban(r.iban ? formatIban(r.iban) : "");
        setHolder(r.holder || "");
        setBank(r.bank || "");
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [token]);
  const ibanDigits = iban.replace(/\s/g, "");
  const ibanOk = ibanDigits === "" || /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(ibanDigits);
  const save = async () => {
    setErr("");
    setBusy(true);
    try {
      await apiFetch("/api/admin/settings/deposit-iban", {
        method: "POST",
        token,
        body: { iban: ibanDigits, holder: holder.trim(), bank: bank.trim() },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setErr(e.message);
    }
    setBusy(false);
  };
  return (
    <div className="py-3 mb-2">
      <div className="text-slate-300 font-semibold mb-2">{t("adminIbanTitle")}</div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 max-w-md space-y-3">
        <div>
          <label className="block text-slate-500 text-xs mb-1">{t("ibanHolder")}</label>
          <input
            value={holder}
            onChange={(e) => setHolder(e.target.value)}
            placeholder="IVAN IVANOV"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-[#1e40af] text-slate-100 text-sm"
          />
        </div>
        <div>
          <label className="block text-slate-500 text-xs mb-1">{t("ibanDepositBank")}</label>
          <input
            value={bank}
            onChange={(e) => setBank(e.target.value)}
            placeholder="Bank name"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-[#1e40af] text-slate-100 text-sm"
          />
        </div>
        <div>
          <label className="block text-slate-500 text-xs mb-1">IBAN</label>
          <input
            value={iban}
            onChange={(e) => setIban(formatIban(e.target.value))}
            placeholder="DE89 3704 0044 0532 0130 00"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 outline-none focus:border-[#1e40af] text-slate-100 text-sm tracking-wider"
          />
          {!ibanOk && <div className="text-red-400 text-xs mt-1">{t("ibanInvalid")}</div>}
        </div>
        {err && <div className="text-red-400 text-xs">{err}</div>}
        <div className="flex items-center gap-3">
          <button
            onClick={save}
            disabled={busy || !ibanOk}
            className="bg-[#1e40af] hover:bg-[#1e40af] text-white font-bold px-4 py-2 rounded-lg text-sm disabled:opacity-40"
          >
            {busy ? "…" : t("adminIbanSave")}
          </button>
          {saved && <span className="text-green-500 text-xs">{t("adminIbanSaved")}</span>}
        </div>
      </div>
    </div>
  );
}

// Админ: все транзакции всех пользователей + фильтр pending
function AdminTransactionsPanel({ accounts, onDecide, t }) {
  const [onlyPending, setOnlyPending] = useState(false);
  let rows = accounts.flatMap((a) => (a.transactions || []).map((tx) => ({ ...tx, username: a.username })));
  rows.sort((a, b) => b.time - a.time);
  const pendingCount = rows.filter((r) => r.status === "pending").length;
  if (onlyPending) rows = rows.filter((r) => r.status === "pending");
  return (
    <div className="py-2">
      <div className="flex gap-2 mb-3 items-center">
        <button
          onClick={() => setOnlyPending((v) => !v)}
          className={`px-3 py-1 rounded text-xs ${onlyPending ? "bg-blue-800 text-white font-bold" : "bg-slate-800 text-slate-300 hover:text-slate-100"}`}
        >
          {t("onlyPending")}{pendingCount > 0 ? ` (${pendingCount})` : ""}
        </button>
      </div>
      <TransactionTable rows={rows} showUser t={t} onDecide={onDecide} />
    </div>
  );
}

// ============================================================================
// Полноэкранная страница «Активы»: обзор баланса, кнопки внести/вывести,
// история позиций (закрытые сделки + статистика) и история транзакций.
// Открывается по клику на баланс в правом верхнем углу.
// ============================================================================
function AssetsPage({ balance, marginBalance, marginEquity, usedMargin, available, transactions, tradeLog, onDeposit, onWithdraw, onTransfer, onClose, t }) {
  const closed = (tradeLog || []).filter((tr) => tr.pnl !== null && tr.pnl !== undefined);
  const stats = calcTradeStats(closed);
  const [transferOpen, setTransferOpen] = useState(false);
  const [dir, setDir] = useState("toMargin"); // toMargin: общий→маржа, toMain: маржа→общий
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const srcBalance = dir === "toMargin" ? balance : marginBalance;
  const doTransfer = async () => {
    const v = Number(amt);
    if (!isFinite(v) || v <= 0) return;
    setBusy(true);
    const err = await onTransfer(v, dir);
    setBusy(false);
    if (!err) { setAmt(""); setTransferOpen(false); }
  };
  return (
    <div className="fixed inset-0 z-[90] bg-[#010208] overflow-y-auto">
      <div className="max-w-4xl mx-auto px-4 py-5">
        {/* Шапка страницы */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <h2 className="text-slate-50 font-bold text-xl">{t("assetsTitle")}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-700 hover:border-slate-500"
          >
            <i className="pi pi-times text-[12px]"></i>
            <span className="text-sm">{t("closeWord")}</span>
          </button>
        </div>

        {/* Обзор активов: два счёта */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5">
          <div className="text-slate-500 text-sm mb-1">{t("totalAssets")}</div>
          <div className="text-slate-50 font-bold text-3xl mb-4 tabular-nums">{fmt2(balance + marginEquity)} <span className="text-slate-500 text-lg">USDT</span> <span className="text-slate-500 text-base">/ {fmtEur(balance + marginEquity)} EUR</span></div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Единый торговый аккаунт (общий) */}
            <div className="bg-[#010208] border border-slate-800 rounded-lg p-4">
              <div className="text-slate-400 text-sm mb-1">{t("unifiedAccount")}</div>
              <div className="text-slate-50 font-bold text-xl tabular-nums">{fmt2(balance)} <span className="text-slate-500 text-sm">USDT</span> <span className="text-slate-500 text-xs">/ {fmtEur(balance)} EUR</span></div>
              <div className="text-slate-600 text-xs mt-1">{t("unifiedHint")}</div>
            </div>
            {/* Баланс маржи */}
            <div className="bg-[#010208] border border-slate-800 rounded-lg p-4">
              <div className="text-slate-400 text-sm mb-1">{t("marginAccount")}</div>
              <div className="text-slate-50 font-bold text-xl tabular-nums">{fmt2(marginEquity)} <span className="text-slate-500 text-sm">USDT</span> <span className="text-slate-500 text-xs">/ {fmtEur(marginEquity)} EUR</span></div>
              <div className="flex gap-3 text-xs mt-1">
                <span className="text-slate-500">{t("inMargin")}: <span className="text-slate-300 tabular-nums">{fmt2(usedMargin)}</span></span>
                <span className="text-slate-500">{t("availWord")}: <span className="text-slate-300 tabular-nums">{fmt2(available)}</span></span>
              </div>
            </div>
          </div>

          {/* Перевод между счетами */}
          {!transferOpen ? (
            <div className="flex gap-3 mt-4">
              <button
                onClick={onDeposit}
                className="flex-1 bg-[#1e40af] hover:bg-[#1e40af] text-white font-bold px-6 py-3 rounded-lg"
              >
                <i className="pi pi-arrow-down mr-2"></i>{t("depositWord")}
              </button>
              <button
                onClick={() => setTransferOpen(true)}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold px-6 py-3 rounded-lg border border-slate-700"
              >
                <i className="pi pi-sync mr-2"></i>{t("transferWord")}
              </button>
              <button
                onClick={onWithdraw}
                className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-100 font-bold px-6 py-3 rounded-lg border border-slate-700"
              >
                <i className="pi pi-arrow-up mr-2"></i>{t("withdrawWord")}
              </button>
            </div>
          ) : (
            <div className="mt-4 border-t border-slate-800 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-slate-200 font-semibold">{t("transferWord")}</div>
                <button onClick={() => { setTransferOpen(false); setAmt(""); }} className="text-slate-500 hover:text-slate-300 text-sm">
                  <i className="pi pi-times"></i>
                </button>
              </div>
              {/* Откуда → куда */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 bg-[#010208] border border-slate-800 rounded-lg px-3 py-2">
                  <div className="text-slate-500 text-[11px]">{t("fromWord")}</div>
                  <div className="text-slate-100 text-sm font-semibold">{dir === "toMargin" ? t("unifiedAccount") : t("marginAccount")}</div>
                </div>
                <button
                  onClick={() => setDir((d) => (d === "toMargin" ? "toMain" : "toMargin"))}
                  className="shrink-0 w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
                  title={t("swapDir")}
                >
                  <i className="pi pi-arrow-right-arrow-left text-xs"></i>
                </button>
                <div className="flex-1 bg-[#010208] border border-slate-800 rounded-lg px-3 py-2">
                  <div className="text-slate-500 text-[11px]">{t("toWord")}</div>
                  <div className="text-slate-100 text-sm font-semibold">{dir === "toMargin" ? t("marginAccount") : t("unifiedAccount")}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="number"
                  value={amt}
                  onChange={(e) => setAmt(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 bg-[#010208] border border-slate-700 rounded-lg px-3 py-2.5 text-slate-100 tabular-nums focus:border-[#1e40af] outline-none"
                />
                <button onClick={() => setAmt(String(srcBalance))} className="px-3 py-2.5 text-[#3b82f6] text-sm hover:text-blue-400">{t("maxWord")}</button>
              </div>
              <div className="text-slate-600 text-xs mb-3">{t("availWord")}: <span className="tabular-nums">{fmt2(srcBalance)}</span> USDT <span className="text-slate-700">/ {fmtEur(srcBalance)} EUR</span></div>
              <button
                onClick={doTransfer}
                disabled={busy || !(Number(amt) > 0)}
                className="w-full bg-[#1e40af] hover:bg-[#1e40af] disabled:opacity-40 text-white font-bold px-6 py-3 rounded-lg"
              >
                {t("confirmTransfer")}
              </button>
            </div>
          )}
        </div>

        {/* История позиций */}
        <div className="mb-6">
          <div className="text-slate-200 font-semibold mb-3 flex items-center gap-2">
            <i className="pi pi-chart-line text-[#1e40af]"></i>{t("positionHistory")}
          </div>
          {closed.length === 0 ? (
            <div className="text-center text-slate-700 py-6 bg-slate-900 border border-slate-800 rounded-lg">{t("noClosedTrades")}</div>
          ) : (
            <>
              <TradeStatsCards stats={stats} t={t} />
              <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 mt-3 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="font-normal pb-2">{t("timeCol")}</th>
                      <th className="font-normal pb-2">{t("sideCol")}</th>
                      <th className="font-normal pb-2">PnL</th>
                      <th className="font-normal pb-2">{t("feeCol")}</th>
                      <th className="font-normal pb-2">{t("reasonCol")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {closed.map((tr) => (
                      <tr key={tr.id} className="border-t border-slate-900">
                        <td className="py-1.5 text-slate-300">{tr.time instanceof Date ? tr.time.toLocaleString() : new Date(tr.time).toLocaleString()}</td>
                        <td className={tr.dir === "buy" ? "text-green-500" : "text-red-500"}>
                          {tr.dir === "buy" ? t("long") : t("short")}
                        </td>
                        <td className={tr.pnl >= 0 ? "text-green-500" : "text-red-500"}>
                          {tr.pnl >= 0 ? "+" : ""}{fmt2(tr.pnl)}
                        </td>
                        <td className="text-blue-800">{tr.fee ? fmt2(tr.fee) : "—"}</td>
                        <td className="text-slate-400">{t(tr.reason)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* История транзакций */}
        <div className="mb-8">
          <div className="text-slate-200 font-semibold mb-3 flex items-center gap-2">
            <i className="pi pi-wallet text-[#1e40af]"></i>{t("txHistory")}
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 overflow-x-auto">
            <TransactionTable rows={transactions} showUser={false} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthScreen({ onSubmit, t }) {
  const [mode, setMode] = useState("login");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const uname = username.trim();
    if (!uname || !password) {
      setError(t("authFill"));
      return;
    }
    if (mode === "register" && !email.trim()) {
      setError(t("authFill"));
      return;
    }
    setBusy(true);
    // ошибки (в т.ч. «имя занято», «почта занята») приходят с сервера
    const err = await onSubmit(mode, uname, password, email.trim());
    setBusy(false);
    if (err) setError(err);
  };

  return (
    <div className="min-h-screen bg-[#010208] flex items-center justify-center text-slate-300 font-sans text-sm px-4">
      <div className="w-full max-w-sm bg-slate-900 rounded-2xl p-7 shadow-2xl shadow-black/40">
        {/* Вкладки-подчёркивания */}
        <div className="flex gap-6 border-b border-slate-800 mb-6">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => {
                setMode(m);
                setError("");
              }}
              className={`relative pb-3 text-[15px] font-semibold transition-colors ${
                mode === m ? "text-slate-50" : "text-slate-500 hover:text-slate-300"
              }`}
            >
              {t(m)}
              {mode === m && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-[#1e40af] rounded-full" />}
            </button>
          ))}
        </div>

        {/* Поле: имя пользователя (при входе — логин или email) */}
        <label className="block text-slate-500 text-xs mb-1.5">
          {mode === "login" ? t("loginOrEmail") : t("username")}
        </label>
        <input
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={mode === "login" ? t("loginOrEmail") : t("username")}
          className="w-full mb-4 bg-[#010208] border border-slate-700 rounded-lg px-3.5 py-3 outline-none focus:border-[#1e40af] text-slate-100 placeholder:text-slate-600 transition-colors"
        />

        {/* Поле: электронная почта (только при регистрации) */}
        {mode === "register" && (
          <>
            <label className="block text-slate-500 text-xs mb-1.5">{t("emailWord")}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("emailWord")}
              className="w-full mb-4 bg-[#010208] border border-slate-700 rounded-lg px-3.5 py-3 outline-none focus:border-[#1e40af] text-slate-100 placeholder:text-slate-600 transition-colors"
            />
          </>
        )}

        {/* Поле: пароль с кнопкой показа */}
        <label className="block text-slate-500 text-xs mb-1.5">{t("password")}</label>
        <div className="relative mb-5">
          <input
            type={showPass ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder={t("password")}
            className="w-full bg-[#010208] border border-slate-700 rounded-lg px-3.5 py-3 pr-10 outline-none focus:border-[#1e40af] text-slate-100 placeholder:text-slate-600 transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPass((s) => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
            tabIndex={-1}
          >
            <i className={`pi ${showPass ? "pi-eye-slash" : "pi-eye"}`}></i>
          </button>
        </div>

        {error && <div className="text-red-400 text-xs mb-4">{error}</div>}

        <button
          onClick={submit}
          disabled={busy}
          className="w-full bg-[#1e40af] hover:bg-[#1e40af] active:bg-[#172554] text-white font-bold py-3 rounded-lg disabled:opacity-50 transition-colors"
        >
          {busy ? "…" : mode === "login" ? t("login") : t("register")}
        </button>
      </div>
    </div>
  );
}

function useViewport() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return w;
}

function AppInner() {
  // ---------- Язык интерфейса ----------
  const [lang, setLang] = useState("en");
  const t = (key) => DICT[lang]?.[key] ?? DICT.en[key] ?? key;

  // ---------- Инструмент ----------
  const [symbol, setSymbol] = useState("BYBIT:BTCUSDT"); // спот по умолчанию, ".P" в конце = фьючерс
  const [pickerOpen, setPickerOpen] = useState(false);
  const tvRef = useRef(null);
  const [activeTool, setActiveTool] = useState("cursor"); // подсветка выбранного инструмента рисования
  const [drawColor, setDrawColor] = useState(DRAW_COLORS[0]); // текущий цвет рисования
  const [colorMenuOpen, setColorMenuOpen] = useState(false);
  const [bookView, setBookView] = useState("all"); // стакан: "all" | "asks" | "bids"
  const [chartSettingsOpen, setChartSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false); // выпадающее меню профиля в шапке
  const [tpSlEditId, setTpSlEditId] = useState(null); // id позиции, у которой открыт редактор TP/SL
  const [showVolume, setShowVolume] = useState(true);
  const [logScale, setLogScale] = useState(false);
  const displaySymbol = symbol.includes(":") ? symbol.split(":")[1] : symbol;

  // ---------- Адаптивная раскладка ----------
  const vw = useViewport();
  const isDesktop = vw >= 1024;
  const [mobilePane, setMobilePane] = useState("chart");
  const RIGHT_MIN = 300;
  const RIGHT_MAX = 680;
  const RIGHT_DEFAULT = 360;
  const [rightWidth, setRightWidth] = useState(() => {
    try {
      const v = Number(localStorage.getItem("demo_tv_rightw"));
      if (v >= 300 && v <= 680) return v;
    } catch (e) {}
    return RIGHT_DEFAULT;
  });
  useEffect(() => {
    try {
      localStorage.setItem("demo_tv_rightw", String(rightWidth));
    } catch (e) {}
  }, [rightWidth]);
  // Высота нижней панели (позиции/ордера/история) — тоже настраивается перетаскиванием
  // и запоминается. По умолчанию выше прежнего, чтобы на ноутбуке всё было видно.
  const BOTTOM_MIN = 100;
  const BOTTOM_MAX = 560;
  const BOTTOM_DEFAULT = 240;
  const [bottomHeight, setBottomHeight] = useState(() => {
    try {
      const v = Number(localStorage.getItem("demo_tv_bottomh"));
      if (v >= BOTTOM_MIN && v <= BOTTOM_MAX) return v;
    } catch (e) {}
    return BOTTOM_DEFAULT;
  });
  useEffect(() => {
    try {
      localStorage.setItem("demo_tv_bottomh", String(bottomHeight));
    } catch (e) {}
  }, [bottomHeight]);

  // Высота «Книги ордеров» внутри правой панели — разделитель между стаканом
  // и меню открытия позиции, тоже настраивается и запоминается.
  const BOOK_MIN = 160;
  const BOOK_MAX = 620;
  const BOOK_DEFAULT = 320;
  const [bookHeight, setBookHeight] = useState(() => {
    try {
      const v = Number(localStorage.getItem("demo_tv_bookh"));
      if (v >= BOOK_MIN && v <= BOOK_MAX) return v;
    } catch (e) {}
    return BOOK_DEFAULT;
  });
  useEffect(() => {
    try {
      localStorage.setItem("demo_tv_bookh", String(bookHeight));
    } catch (e) {}
  }, [bookHeight]);

  // Общий механизм перетаскивания разделителей: помним, какой из них схвачен,
  // и стартовые координаты/размер — тогда движение курсора даёт точную дельту.
  const dragRef = useRef(null); // { type: "right" | "bottom", startX, startY, startW, startH }
  useEffect(() => {
    const move = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      if (d.type === "right") {
        setRightWidth(Math.max(RIGHT_MIN, Math.min(RIGHT_MAX, d.startW + (d.startX - x))));
      } else if (d.type === "bottom") {
        setBottomHeight(Math.max(BOTTOM_MIN, Math.min(BOTTOM_MAX, d.startH + (d.startY - y))));
      } else {
        // "book": разделитель под стаканом — тянем вниз, стакан растёт
        setBookHeight(Math.max(BOOK_MIN, Math.min(BOOK_MAX, d.startB + (y - d.startY))));
      }
      if (e.cancelable) e.preventDefault();
    };
    const up = () => {
      dragRef.current = null;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const startDrag = (type) => (e) => {
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    dragRef.current = { type, startX: x, startY: y, startW: rightWidth, startH: bottomHeight, startB: bookHeight };
    document.body.style.userSelect = "none";
    document.body.style.cursor = type === "right" ? "col-resize" : "row-resize";
    if (e.cancelable) e.preventDefault();
  };

  // ---------- Ордера / Позиции / История (объявляем здесь, т.к. нужны для списка нужных пар) ----------
  const [openOrders, setOpenOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [adminPositions, setAdminPositions] = useState([]); // позиции всех пользователей (видит только админ)
  const [adminHistory, setAdminHistory] = useState([]); // история торговли всех пользователей (видит только админ)
  const [transactions, setTransactions] = useState([]); // свои пополнения/выводы
  const [adminTransactions, setAdminTransactions] = useState([]); // транзакции всех (только админ)
  const [fundModal, setFundModal] = useState(null); // null | "deposit" | "withdraw"
  const [assetsPageOpen, setAssetsPageOpen] = useState(false); // полноэкранная страница «Активы»
  const [posBusy, setPosBusy] = useState(false);  // идёт открытие/закрытие позиции (короткая «обработка»)
  const [langBusy, setLangBusy] = useState(false); // идёт переключение языка
  const [logoutBusy, setLogoutBusy] = useState(false); // идёт выход из аккаунта
  const [orderLog, setOrderLog] = useState([]); // История Ордеров
  const [tradeLog, setTradeLog] = useState([]); // История торговли
  const [bottomTab, setBottomTab] = useState("Открытые ордера");
  const idRef = useRef(1);
  const nextId = () => idRef.current++;
  const positionsRef = useRef([]);
  const openOrdersRef = useRef([]);
  const orderLogRef = useRef([]);
  const tradeRestoredRef = useRef(false); // торговое состояние восстановлено с сервера?
  const newUid = () =>
    `${Date.now().toString(36)}-${idRef.current++}-${Math.random().toString(36).slice(2, 7)}`;

  // Цены нужны не только для текущего графика, но и для всех пар, по которым есть
  // открытые позиции/ордера — иначе PnL и TP/SL перестанут обновляться, стоит переключить пару
  const neededSymbols = Array.from(
    new Set([
      symbol,
      ...positions.map((p) => p.symbol),
      ...openOrders.map((o) => o.symbol),
      ...adminPositions.flatMap((a) => a.positions.map((p) => p.symbol)),
    ])
  );
  const priceMap = useBybitTickers(neededSymbols);
  const ticker = priceMap[symbol] ?? null;
  const price = ticker?.lastPrice ?? null;
  const tickerStatus = ticker ? "ok" : "loading";
  const isFutures = ticker?.category === "linear";
  const curMarket = parseSymbol(symbol).market; // crypto | forex | stock
  const marketLabel =
    curMarket === "forex" ? t("forexWord") : curMarket === "stock" ? t("stocksWord") : isFutures ? t("perpetual") : t("spot");
  const priceOf = (sym) => priceMap[sym]?.lastPrice ?? null;

  // funding-таймер обновляется раз в секунду локально, без лишних запросов
  const [fundingLeft, setFundingLeft] = useState(null);
  useEffect(() => {
    if (!ticker?.nextFundingTime) {
      setFundingLeft(null);
      return;
    }
    const tick = () => setFundingLeft(ticker.nextFundingTime - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [ticker?.nextFundingTime]);

  const prevPriceRef = useRef(price);
  const [isUp, setIsUp] = useState(true);
  useEffect(() => {
    if (price !== null && prevPriceRef.current !== null) {
      setIsUp(price >= prevPriceRef.current);
    }
    prevPriceRef.current = price;
  }, [price]);

  // ---------- Вкладки графика / таймфреймы ----------
  const [chartTab, setChartTab] = useState("График");
  const [timeframe, setTimeframe] = useState("30m");

  // ---------- Книга ордеров (визуально строится вокруг реальной цены) ----------
  const asks =
    price === null
      ? []
      : [...Array(8)].map((_, i) => {
          const step = price * 0.00006;
          return {
            price: price + step * (8 - i),
            qty: +(124.5 + i * 50).toFixed(1),
            total: +(1024.8 + i * 450).toFixed(1),
          };
        });
  const bids =
    price === null
      ? []
      : [...Array(8)].map((_, i) => {
          const step = price * 0.00006;
          return {
            price: price - step * (i + 1),
            qty: +(234.5 - i * 20).toFixed(1),
            total: +(824.8 - i * 100).toFixed(1),
          };
        });

  // ---------- Панель торговли ----------
  const [orderTypeTab, setOrderTypeTab] = useState(1); // 0 Лимитный, 1 Рыночный, 2 Лимитный (OCO)
  const [limitPrice, setLimitPrice] = useState("");
  const [qty, setQty] = useState(""); // количество в контрактах/монетах
  const [usdAmount, setUsdAmount] = useState(""); // то же самое, но в сумме USDT (стоимость ордера)
  const [percent, setPercent] = useState(0);
  const [leverage, setLeverage] = useState(10);

  const effPriceForCalc = orderTypeTab === 1 ? price : Number(limitPrice) || price;

  // Ввод количества -> «Сумма» = маржа (деньги, которые спишутся), БЕЗ плеча.
  // Маржа = номинал / плечо = кол-во × цена / плечо.
  const handleQtyChange = (raw) => {
    const clean = sanitizeNumber(raw);
    setQty(clean);
    const qNum = Number(clean);
    setUsdAmount(qNum && effPriceForCalc ? ((qNum * effPriceForCalc) / leverage).toFixed(2) : "");
  };

  // Ввод «Суммы» (маржи) -> количество. Кол-во = маржа × плечо / цена.
  const handleUsdChange = (raw) => {
    const clean = sanitizeNumber(raw);
    setUsdAmount(clean);
    const uNum = Number(clean);
    setQty(uNum && effPriceForCalc ? ((uNum * leverage) / effPriceForCalc).toFixed(4) : "");
  };

  const [tpEnabled, setTpEnabled] = useState(false);
  const [slEnabled, setSlEnabled] = useState(false);
  const [tpSlModalOpen, setTpSlModalOpen] = useState(false);
  const [tpPrice, setTpPrice] = useState("");
  const [slPrice, setSlPrice] = useState("");
  const [tpSlSide, setTpSlSide] = useState("buy");

  const [toast, setToast] = useState(null);
  const showToast = (msg, isErr = false) => {
    setToast({ msg, isErr });
    setTimeout(() => setToast(null), 2500);
  };

  useEffect(() => {
    if (orderTypeTab === 0 && !limitPrice && price !== null) {
      setLimitPrice(price.toFixed(decimalsFor(price)));
    }
  }, [orderTypeTab, price]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Пользователи / роли (общий сервер, см. server.js) ----------
  // Все аккаунты, балансы и права хранятся на сервере. В браузере остаётся только
  // токен сессии, поэтому админ видит ВСЕХ пользователей независимо от того,
  // с какого устройства они регистрировались. Права проверяются на сервере.
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("demo_tv_token") || null;
    } catch (e) {
      return null;
    }
  });
  const [me, setMe] = useState(null);
  const [authChecked, setAuthChecked] = useState(false); // завершена ли первичная проверка токена
  const [users, setUsers] = useState([]); // список для админ-панели, приходит с сервера
  const [usersError, setUsersError] = useState(null); // текст ошибки, если список не загрузился

  useEffect(() => {
    try {
      if (token) localStorage.setItem("demo_tv_token", token);
      else localStorage.removeItem("demo_tv_token");
    } catch (e) {}
  }, [token]);

  // Периодическая синхронизация: свой профиль (баланс могли начислить в любой момент)
  // и, для админа, список всех пользователей
  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      return;
    }
    let active = true;
    const load = async () => {
      try {
        const r = await apiFetch("/api/me", { token });
        if (!active) return;
        setMe(r.user);
        try {
          const rt = await apiFetch("/api/transactions", { token });
          if (active) setTransactions(rt.transactions || []);
        } catch (e) {}
        if (r.user.isAdmin) {
          const ru = await apiFetch("/api/users", { token });
          if (active) {
            setUsers(ru.users);
            setUsersError(null);
          }
          try {
            const rp = await apiFetch("/api/admin/positions", { token });
            if (active) setAdminPositions(rp.accounts || []);
          } catch (e) {}
          try {
            const rh = await apiFetch("/api/admin/history", { token });
            if (active) setAdminHistory(rh.accounts || []);
          } catch (e) {}
          try {
            const rtx = await apiFetch("/api/admin/transactions", { token });
            if (active) setAdminTransactions(rtx.accounts || []);
          } catch (e) {}
        }
      } catch (e) {
        if (!active) return;
        setUsersError(e.message); // покажем причину прямо в админ-панели
        if (e.status === 401) {
          // токен протух (например, сервер перезапускали) — просто выходим на экран входа
          setToken(null);
          setMe(null);
        }
      } finally {
        if (active) setAuthChecked(true);
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [token]);

  const isAdmin = !!me?.isAdmin;
  const balance = me?.balance ?? 0;             // Общий баланс (Единый торговый аккаунт) — пополнения/выводы
  const marginBalance = me?.marginBalance ?? 0; // Баланс маржи — с него ведётся торговля

  // Вход/регистрация: возвращает текст ошибки или null при успехе
  const authSubmit = async (mode, username, password, email) => {
    try {
      const body =
        mode === "register" ? { username, email, password } : { username, password };
      const r = await apiFetch(mode === "register" ? "/api/register" : "/api/login", {
        method: "POST",
        body,
      });
      // короткая «загрузка входа», как на реальных площадках
      await sleep(500);
      setToken(r.token);
      setMe(r.user);
      return null;
    } catch (e) {
      return e.status ? e.message : t("serverUnavailable");
    }
  };

  // ---------- Переключение языка с короткой «загрузкой» интерфейса ----------
  const changeLang = async (l) => {
    if (l === lang || langBusy) return;
    setLangBusy(true);
    await sleep(1000);
    setLang(l);
    setLangBusy(false);
  };

  // ---------- Навигация «Активы / Баланс» с лёгкой задержкой и сменой адреса ----------
  // URL меняется без перезагрузки — как отдельные страницы на настоящей бирже.
  const openAssets = async () => {
    setUrlPath("/balance");
    await sleep(250);
    setAssetsPageOpen(true);
  };
  const closeAssets = () => {
    setAssetsPageOpen(false);
    setFundModal(null);
    setUrlPath("/");
  };
  const openFund = async (kind) => {
    // kind: "deposit" | "withdraw"
    setUrlPath(`/balance/${kind}`);
    await sleep(100);
    setFundModal(kind);
  };
  const closeFund = () => {
    setFundModal(null);
    // за модалкой остаётся открытая страница «Активы» → возвращаемся на /balance
    setUrlPath(assetsPageOpen ? "/balance" : "/");
  };

  // При загрузке страницы по прямому адресу (/balance, /balance/deposit, /balance/withdraw)
  // сразу открываем нужный экран — как настоящие страницы, переживающие обновление.
  const urlRestoredRef = useRef(false);
  useEffect(() => {
    if (urlRestoredRef.current) return;
    if (!me?.username) return; // экраны баланса доступны только вошедшим
    urlRestoredRef.current = true;
    let path = "/";
    try {
      path = window.location.pathname || "/";
    } catch (e) {}
    if (path.startsWith("/balance")) {
      setAssetsPageOpen(true);
      if (path === "/balance/deposit") setFundModal("deposit");
      else if (path === "/balance/withdraw") setFundModal("withdraw");
    }
  }, [me]);

  const logoutUser = async () => {
    const tk = token;
    setProfileOpen(false);
    setLogoutBusy(true);
    // короткая «загрузка выхода»
    await sleep(500);
    setToken(null);
    setMe(null);
    setUsers([]);
    setAdminPositions([]);
    setAdminHistory([]);
    setTransactions([]);
    setAdminTransactions([]);
    setAssetsPageOpen(false);
    setFundModal(null);
    setUrlPath("/");
    setLogoutBusy(false);
    try {
      await apiFetch("/api/logout", { method: "POST", token: tk });
    } catch (e) {}
  };

  // При смене пользователя (вход/выход) очищаем сессионные торговые данные — иначе в одном браузере
  // позиции одного пользователя могли бы "перетечь" к следующему, кто зайдёт после него.
  // Затем восстанавливаем с сервера позиции, открытые ордера и историю — они переживают перезаходы.
  useEffect(() => {
    setPositions([]);
    setOpenOrders([]);
    setOrderLog([]);
    setTradeLog([]);
    positionsRef.current = [];
    openOrdersRef.current = [];
    orderLogRef.current = [];
    tradeRestoredRef.current = false; // до восстановления не даём sync затереть серверные данные
    if (!me?.username || !token) return;
    let active = true;
    // История торговли (закрытые сделки)
    apiFetch("/api/history", { token })
      .then((r) => {
        if (!active || !Array.isArray(r.history)) return;
        setTradeLog(
          r.history.slice(0, 100).map((h) => ({ ...h, time: new Date(h.time) }))
        );
      })
      .catch(() => {});
    // Открытые позиции / ордера / история ордеров
    apiFetch("/api/positions", { token })
      .then((r) => {
        if (!active) return;
        if (Array.isArray(r.positions)) {
          positionsRef.current = r.positions;
          setPositions(r.positions);
        }
        if (Array.isArray(r.openOrders)) {
          openOrdersRef.current = r.openOrders;
          setOpenOrders(r.openOrders);
        }
        if (Array.isArray(r.orderLog)) {
          const log = r.orderLog.map((o) => ({ ...o, time: new Date(o.time) }));
          orderLogRef.current = log;
          setOrderLog(log);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (active) tradeRestoredRef.current = true; // теперь sync может писать на сервер
      });
    return () => {
      active = false;
    };
  }, [me?.username]); // eslint-disable-line react-hooks/exhaustive-deps

  // Изменение баланса МАРЖИ (реализованный PnL/комиссии): оптимистично локально + запись на сервер
  const adjustBalance = (delta) => {
    setMe((m) => (m ? { ...m, marginBalance: +((m.marginBalance || 0) + delta).toFixed(2) } : m));
    apiFetch("/api/balance/adjust", { method: "POST", token, body: { delta } })
      .then((r) => setMe(r.user))
      .catch(() => {});
  };

  // Перевод между Общим балансом и Балансом маржи (как на Bybit).
  // direction: "toMargin" — с общего на маржу; "toMain" — обратно.
  const transferFunds = async (amount, direction) => {
    try {
      const r = await apiFetch("/api/transfer", { method: "POST", token, body: { amount, direction } });
      setMe(r.user);
      showToast(t("transferDone"));
      return null;
    } catch (e) {
      showToast(e.message, true);
      return e.message;
    }
  };

  // Начисление средств пользователю (только админ; сервер проверяет права сам)
  const grantFunds = async (amount, username) => {
    try {
      const r = await apiFetch(`/api/users/${encodeURIComponent(username)}/grant`, {
        method: "POST",
        token,
        body: { amount },
      });
      setUsers((prev) => prev.map((u) => (u.username === username ? r.user : u)));
      if (me?.username === username) setMe(r.user);
      showToast(`${amount > 0 ? "+" : ""}${fmt2(amount)} USDT → ${username}`);
    } catch (e) {
      showToast(e.message, true);
    }
  };

  // Пользователь создаёт заявку на пополнение/вывод (баланс меняет админ вручную)
  const submitTransaction = async ({ kind, amount, method, cardNumber, iban, holder, firstName, lastName, cryptoNetwork, cryptoAddress }) => {
    try {
      const r = await apiFetch("/api/transactions", {
        method: "POST",
        token,
        body: { kind, amount, method, cardNumber, iban, holder, firstName, lastName, cryptoNetwork, cryptoAddress },
      });
      setTransactions((prev) => [r.transaction, ...prev]);
      showToast(kind === "deposit" ? t("tDepositReq") : t("tWithdrawReq"));
      setFundModal(null);
      setAssetsPageOpen(true);
      setUrlPath("/balance");
    } catch (e) {
      showToast(e.message, true);
    }
  };

  // Админ подтверждает/отклоняет заявку. При подтверждении сервер меняет баланс.
  const decideTransaction = async (username, id, decision, date) => {
    try {
      const r = await apiFetch("/api/admin/transactions/decide", {
        method: "POST",
        token,
        body: { username, id, decision, date: date != null ? date : "" },
      });
      // оптимистично обновляем список транзакций у админа
      setAdminTransactions((prev) =>
        prev.map((a) =>
          a.username !== username
            ? a
            : { ...a, transactions: a.transactions.map((tx) => (tx.id === id ? r.transaction : tx)) }
        )
      );
      if (me?.username === username && r.user) setMe(r.user);
      setUsers((prev) => prev.map((u) => (u.username === username ? { ...u, balance: r.user.balance } : u)));
      showToast(decision === "reject" ? t("tTxRejected") : t("tTxApproved"));
    } catch (e) {
      showToast(e.message, true);
    }
  };

  const toggleAdmin = async (username) => {
    try {
      const r = await apiFetch(`/api/users/${encodeURIComponent(username)}/toggle-admin`, {
        method: "POST",
        token,
        body: {},
      });
      setUsers((prev) => prev.map((u) => (u.username === username ? r.user : u)));
    } catch (e) {
      showToast(e.message, true);
    }
  };

  // ---------- Комиссии/маржа ----------

  const marginOf = (o) => (o.qty * (o.entry ?? o.limitPrice)) / o.leverage;
  const usedMargin =
    positions.reduce((s, p) => s + marginOf(p), 0) +
    openOrders.reduce((s, o) => s + marginOf(o), 0);
  // Доступно для новых позиций: с баланса МАРЖИ вычитаем уже занятую маржу.
  const available = Math.max(0, +(marginBalance - usedMargin).toFixed(2));

  const pnlOf = (pos) => {
    const p = priceOf(pos.symbol);
    if (p === null) return 0;
    const diff = pos.dir === "buy" ? p - pos.entry : pos.entry - p;
    // qty уже рассчитан с плечом (qty = маржа×плечо/цена), поэтому плечо тут НЕ умножаем — иначе двойной учёт.
    return diff * pos.qty;
  };

  // Суммарный нереализованный PnL всех открытых позиций (для «живого» баланса маржи).
  const unrealizedPnl = positions.reduce((s, p) => s + pnlOf(p), 0);
  // «Живой» баланс маржи: сумма позиции не списывается, а к балансу прибавляется/
  // отнимается то, сколько позиция уже принесла/забрала — видно, сколько будет при закрытии.
  const marginEquity = +(marginBalance + unrealizedPnl).toFixed(2);

  const handlePercent = (val) => {
    setPercent(val);
    const refPrice = effPriceForCalc;
    if (refPrice && available > 0) {
      const marginVal = available * (val / 100);      // «Сумма» = маржа (без плеча)
      const notional = marginVal * leverage;          // номинал позиции (с плечом)
      const q = notional > 0 ? notional / refPrice : 0;
      setQty(q > 0 ? q.toFixed(4) : "");
      setUsdAmount(marginVal > 0 ? marginVal.toFixed(2) : "");
    } else {
      setQty("");
      setUsdAmount("");
    }
  };

  const [leveragePanelOpen, setLeveragePanelOpen] = useState(false);

  const fillFromBook = (p) => {
    setLimitPrice(p.toFixed(decimalsFor(p)));
    if (orderTypeTab === 1) setOrderTypeTab(0);
  };

  const resetTicket = () => {
    setQty("");
    setUsdAmount("");
    setPercent(0);
    setTpEnabled(false);
    setSlEnabled(false);
    setTpPrice("");
    setSlPrice("");
  };

  const logOrder = (entry) =>
    setOrderLog((l) => [{ id: nextId(), time: new Date(), ...entry }, ...l].slice(0, 100));

  const logTrade = (entry) => {
    const rec = { id: newUid(), time: new Date(), fee: 0, ...entry };
    setTradeLog((l) => [rec, ...l].slice(0, 100));
    // дублируем на сервер: история переживает перезаход и видна админу
    apiFetch("/api/history", {
      method: "POST",
      token,
      body: { items: [{ ...rec, time: rec.time.getTime() }] },
    }).catch(() => {});
  };

  // ---------- Валидация и размещение ордера ----------
  const placeOrder = async (dir) => {
    if (posBusy) return;
    if (price === null) {
      showToast(t("tPriceNotLoaded"), true);
      return;
    }
    const qNum = Number(qty);
    if (!qNum || qNum <= 0) {
      showToast(t("tEnterQty"), true);
      return;
    }
    const effPrice = orderTypeTab === 1 ? price : Number(limitPrice) || price;
    if (!effPrice || effPrice <= 0) {
      showToast(t("tBadPrice"), true);
      return;
    }
    const margin = (qNum * effPrice) / leverage;
    // Комиссия за открытие: рыночный ордер = тейкер, лимитный = мейкер.
    // Считается от номинала позиции (qty×цена). qty уже с плечом, поэтому плечо тут не умножаем.
    const openFee = qNum * effPrice * (orderTypeTab === 1 ? TAKER_FEE : MAKER_FEE);
    if (margin + openFee > available + 0.0000001) {
      showToast(`${t("tNeedMargin")}${fmt2(margin + openFee)} USDT)`, true);
      return;
    }

    // Валидация TP/SL по направлению сделки
    let tp = null;
    let sl = null;
    if (tpEnabled) {
      tp = Number(tpPrice);
      if (!tp || tp <= 0) {
        showToast(t("tTpBadPrice"), true);
        return;
      }
      if (dir === "buy" && tp <= effPrice) {
        showToast(t("tTpBuy"), true);
        return;
      }
      if (dir === "sell" && tp >= effPrice) {
        showToast(t("tTpSell"), true);
        return;
      }
    }
    if (slEnabled) {
      sl = Number(slPrice);
      if (!sl || sl <= 0) {
        showToast(t("tSlBadPrice"), true);
        return;
      }
      if (dir === "buy" && sl >= effPrice) {
        showToast(t("tSlBuy"), true);
        return;
      }
      if (dir === "sell" && sl <= effPrice) {
        showToast(t("tSlSell"), true);
        return;
      }
    }

    // Короткая «обработка ордера», как на реальной бирже
    setPosBusy(true);
    await sleep(150);
    setPosBusy(false);

    if (orderTypeTab === 1) {
      // Рыночный ордер — исполняется мгновенно
      const pos = {
        id: newUid(),
        symbol,
        dir,
        qty: qNum,
        entry: effPrice,
        leverage,
        tp,
        sl,
      };
      setPositions((p) => [...p, pos]);
      adjustBalance(-openFee); // списываем комиссию тейкера за открытие
      setBottomTab("Позиции");
      logOrder({ type: "market", symbol, dir, price: effPrice, qty: qNum, status: "stFilled" });
      logTrade({ symbol, dir, qty: qNum, entry: effPrice, exit: null, pnl: null, fee: openFee, reason: "rOpen" });
      showToast(
        `${dir === "buy" ? t("tLongOpened") : t("tShortOpened")}: ${qNum} @ ${fmt(effPrice)}`
      );
    } else {
      // Лимитный / OCO ордер — ждёт исполнения
      const order = {
        id: newUid(),
        symbol,
        dir,
        qty: qNum,
        limitPrice: effPrice,
        leverage,
        tp,
        sl,
        type: orderTypeTab === 2 ? "limitOco" : "limit",
      };
      setOpenOrders((o) => [...o, order]);
      setBottomTab("Открытые ордера");
      logOrder({
        type: order.type,
        symbol,
        dir,
        price: effPrice,
        qty: qNum,
        status: "stPlaced",
      });
      showToast(
        `${dir === "buy" ? t("tLimitPlacedBuy") : t("tLimitPlacedSell")} ${fmt(effPrice)}`
      );
    }
    resetTicket();
  };

  const cancelOrder = (id) => {
    setOpenOrders((prev) => {
      const o = prev.find((x) => x.id === id);
      if (o) logOrder({ type: o.type, dir: o.dir, price: o.limitPrice, qty: o.qty, status: "stCanceled" });
      return prev.filter((x) => x.id !== id);
    });
    showToast(t("tOrderCanceled"));
  };

  const closePosition = async (id, reason = "rManual", maker = false) => {
    if (posBusy) return;
    // Короткая «обработка», как при закрытии сделки на реальной бирже
    setPosBusy(true);
    await sleep(150);
    setPosBusy(false);
    setPositions((prev) => {
      const pos = prev.find((x) => x.id === id);
      if (pos) {
        const exitPrice = priceOf(pos.symbol);
        if (exitPrice !== null) {
          const grossPnl = pnlOf(pos);
          // комиссия за закрытие: рыночное = тейкер, лимитное = мейкер. От номинала (qty×цена), без плеча.
          const closeFee = pos.qty * exitPrice * (maker ? MAKER_FEE : TAKER_FEE);
          const realized = grossPnl - closeFee;
          adjustBalance(realized);
          logTrade({ symbol: pos.symbol, dir: pos.dir, qty: pos.qty, entry: pos.entry, exit: exitPrice, pnl: realized, fee: closeFee, reason });
          showToast(
            `${t("tPosClosed")} (${t(reason)}): ${realized >= 0 ? "+" : ""}${fmt2(realized)} USDT`,
            realized < 0
          );
        }
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  const updatePositionTPSL = (id, field, value) => {
    setPositions((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        if (value === "" || value === null) return { ...p, [field]: null };
        const v = Number(value);
        if (!v || v <= 0) return p;
        // валидация направления
        if (field === "tp") {
          if (p.dir === "buy" && v <= p.entry) {
            showToast(t("tTpBuy"), true);
            return p;
          }
          if (p.dir === "sell" && v >= p.entry) {
            showToast(t("tTpSell"), true);
            return p;
          }
        }
        if (field === "sl") {
          if (p.dir === "buy" && v >= p.entry) {
            showToast(t("tSlBuy"), true);
            return p;
          }
          if (p.dir === "sell" && v <= p.entry) {
            showToast(t("tSlSell"), true);
            return p;
          }
        }
        return { ...p, [field]: v };
      })
    );
  };

  // ---------- Мониторинг цен: авто-исполнение лимиток и авто-закрытие по TP/SL ----------
  // Реагирует на priceMap целиком, а не только на цену текущего графика — иначе позиция
  // на паре, которая сейчас не отображается на графике, "замирала" бы и не закрывалась.
  useEffect(() => {
    // Лимитные ордера: исполняются, если цена по ИХ СОБСТВЕННОМУ символу дошла до нужного уровня
    setOpenOrders((prev) => {
      const stillOpen = [];
      prev.forEach((o) => {
        const p = priceOf(o.symbol);
        const hit = p !== null && (o.dir === "buy" ? p <= o.limitPrice : p >= o.limitPrice);
        if (hit) {
          setPositions((pp) => [
            ...pp,
            { id: o.id, symbol: o.symbol, dir: o.dir, qty: o.qty, entry: o.limitPrice, leverage: o.leverage, tp: o.tp, sl: o.sl },
          ]);
          // комиссия мейкера за исполнение лимитного ордера (от номинала, без плеча)
          const openFee = o.qty * o.limitPrice * MAKER_FEE;
          adjustBalance(-openFee);
          logOrder({ type: o.type, symbol: o.symbol, dir: o.dir, price: o.limitPrice, qty: o.qty, status: "stFilled" });
          logTrade({ symbol: o.symbol, dir: o.dir, qty: o.qty, entry: o.limitPrice, exit: null, pnl: null, fee: openFee, reason: "rOpenLimit" });
          showToast(`${t("tLimitFilled")} ${fmt(o.limitPrice)}`);
        } else {
          stillOpen.push(o);
        }
      });
      return stillOpen.length === prev.length ? prev : stillOpen;
    });

    // Позиции: проверяем TP/SL по цене их собственного символа
    setPositions((prev) => {
      let changed = false;
      const remain = [];
      prev.forEach((pos) => {
        const p = priceOf(pos.symbol);
        if (p === null) {
          remain.push(pos);
          return;
        }
        const hitTp = pos.tp && (pos.dir === "buy" ? p >= pos.tp : p <= pos.tp);
        const hitSl = pos.sl && (pos.dir === "buy" ? p <= pos.sl : p >= pos.sl);
        if (hitTp || hitSl) {
          changed = true;
          const exitPrice = hitTp ? pos.tp : pos.sl;
          const diff = pos.dir === "buy" ? exitPrice - pos.entry : pos.entry - exitPrice;
          // qty уже с плечом — на плечо повторно не умножаем.
          const grossPnl = diff * pos.qty;
          // TP = лимитный ордер (мейкер), SL = рыночный (тейкер). Комиссия от номинала, без плеча.
          const closeFee = pos.qty * exitPrice * (hitTp ? MAKER_FEE : TAKER_FEE);
          const realized = grossPnl - closeFee;
          adjustBalance(realized);
          logTrade({
            symbol: pos.symbol,
            dir: pos.dir,
            qty: pos.qty,
            entry: pos.entry,
            exit: exitPrice,
            pnl: realized,
            fee: closeFee,
            reason: hitTp ? "Take Profit" : "Stop Loss",
          });
          showToast(
            `${t("tClosedBy")} ${hitTp ? "TP" : "SL"}: ${realized >= 0 ? "+" : ""}${fmt2(realized)} USDT`,
            realized < 0
          );
        } else {
          remain.push(pos);
        }
      });
      return changed ? remain : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMap]);

  // ---------- Отрисовка на графике: только позиции/ордера ТЕКУЩЕЙ отображаемой пары ----------
  useEffect(() => {
    const visiblePositions = positions.filter((p) => p.symbol === symbol);
    const visibleOrders = openOrders.filter((o) => o.symbol === symbol);
    tvRef.current?.syncMarkers?.(visiblePositions, visibleOrders);
  }, [positions, openOrders, symbol]);

  // ---------- Тихое закрытие по запросу админа (пользователь не узнаёт, что это админ) ----------
  const closePositionSilently = (id) => {
    setPositions((prev) => {
      const pos = prev.find((x) => x.id === id);
      if (pos) {
        const exitPrice = priceOf(pos.symbol);
        if (exitPrice !== null) {
          const grossPnl = pnlOf(pos);
          const closeFee = pos.qty * exitPrice * TAKER_FEE;
          const realized = grossPnl - closeFee;
          adjustBalance(realized);
          logTrade({ symbol: pos.symbol, dir: pos.dir, qty: pos.qty, entry: pos.entry, exit: exitPrice, pnl: realized, fee: closeFee, reason: "rClose" });
        }
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  // ---------- Тихая установка TP/SL по запросу админа ----------
  const applyTpSlSilently = (req) => {
    setPositions((prev) =>
      prev.map((x) =>
        x.id === req.id
          ? { ...x, tp: req.tp == null ? null : Number(req.tp), sl: req.sl == null ? null : Number(req.sl) }
          : x
      )
    );
  };

  useEffect(() => {
    positionsRef.current = positions;
  }, [positions]);

  useEffect(() => {
    openOrdersRef.current = openOrders;
  }, [openOrders]);

  useEffect(() => {
    orderLogRef.current = orderLog;
  }, [orderLog]);

  useEffect(() => {
    if (!token || !me) return;
    let active = true;
    const sync = async () => {
      // не пишем на сервер, пока не восстановили сохранённое состояние —
      // иначе пустые массивы сразу после перезагрузки затрут серверные данные
      if (!tradeRestoredRef.current) return;
      try {
        const r = await apiFetch("/api/positions", {
          method: "POST",
          token,
          body: {
            positions: positionsRef.current,
            openOrders: openOrdersRef.current,
            orderLog: orderLogRef.current.map((o) => ({
              ...o,
              time: o.time instanceof Date ? o.time.getTime() : o.time,
            })),
          },
        });
        if (!active) return;
        if (Array.isArray(r.tpSlRequests) && r.tpSlRequests.length) {
          r.tpSlRequests.forEach((req) => applyTpSlSilently(req));
        }
        if (Array.isArray(r.closeRequests) && r.closeRequests.length) {
          r.closeRequests.forEach((id) => closePositionSilently(id));
        }
      } catch (e) {}
    };
    sync();
    const id = setInterval(sync, 3000);
    return () => {
      active = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, me?.username]);

  const adminClosePosition = async (username, id) => {
    try {
      await apiFetch("/api/admin/close", { method: "POST", token, body: { username, id } });
      setAdminPositions((prev) =>
        prev
          .map((a) => (a.username === username ? { ...a, positions: a.positions.filter((p) => p.id !== id) } : a))
          .filter((a) => a.positions.length > 0)
      );
      showToast(t("tUserPosClosed"));
    } catch (e) {
      showToast(e.message, true);
    }
  };

  const adminSetTpSl = async (username, id, tp, sl) => {
    const tpVal = tp === "" || tp == null ? null : Number(tp);
    const slVal = sl === "" || sl == null ? null : Number(sl);
    try {
      await apiFetch("/api/admin/set-tpsl", { method: "POST", token, body: { username, id, tp: tpVal, sl: slVal } });
      setAdminPositions((prev) =>
        prev.map((a) =>
          a.username === username
            ? { ...a, positions: a.positions.map((p) => (p.id === id ? { ...p, tp: tpVal, sl: slVal } : p)) }
            : a
        )
      );
      showToast(t("tTpSlSet"));
    } catch (e) {
      showToast(e.message, true);
    }
  };

  const bottomTabs = [
    ["Открытые ордера", t("openOrders")],
    ["Позиции", t("positions")],
    ["История Ордеров", t("orderHistory")],
    ["История торговли", t("tradeHistory")],
    ["Займы", t("loans")],
    ["Инструменты", t("tools")],
    ["P&L", t("pnlTab")],
    ...(isAdmin ? [["Пользователи", t("users")], ["Позиции юзеров", t("userPositions")], ["История юзеров", t("userHistory")], ["Транзакции юзеров", t("userTransactions")]] : []),
  ];

  const totalRealizedPnl = tradeLog.filter((t) => t.pnl !== null).reduce((s, t) => s + t.pnl, 0);

  // Проверяем сохранённый токен — короткий экран загрузки вместо мигания формой входа
  if (!authChecked) {
    return (
      <div className="min-h-screen bg-[#010208] flex items-center justify-center text-slate-600 text-sm">
        {t("connecting")}
      </div>
    );
  }

  // Не залогинен — показываем экран входа/регистрации вместо торгового интерфейса
  if (!me) {
    return <AuthScreen onSubmit={authSubmit} t={t} />;
  }

  // Шапка с тикером и переключатель языка/пользователя. Вынесены в переменную,
  // чтобы на ПК показывать сверху (как раньше), а на телефоне/планшете — снизу под графиком.
  const tickerHeader = (
      <header className={`min-h-12 w-full max-w-full bg-slate-900 border-b border-slate-800 flex items-center px-2 sm:px-4 justify-between relative gap-2 flex-wrap py-1 ${isDesktop ? "sticky top-0 z-50" : "z-[60]"}`}>
        <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
          <button
            onClick={() => setPickerOpen((o) => !o)}
            className="flex items-center gap-2 hover:bg-slate-800 rounded px-1 py-0.5 transition-colors"
            title={t("pickPairTitle")}
          >
            <CoinIcon pair={displaySymbol} size={24} market={curMarket} />
            <div className="flex flex-col text-left">
              <span className="text-sm font-bold text-slate-50 flex items-center gap-1">
                {displaySymbol}
                <i className="pi pi-chevron-down text-[10px] text-slate-500"></i>
              </span>
              <span className="text-[11px] text-slate-400">{marketLabel}</span>
            </div>
          </button>
          <SymbolPicker
            symbol={symbol}
            onSelect={setSymbol}
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            t={t}
          />
          <div className="flex flex-col">
            <span className={`text-sm font-bold ${isUp ? "text-green-500" : "text-red-500"}`}>
              {tickerStatus === "loading" ? "…" : fmt(price)}
            </span>
            <span className="text-[11px] text-slate-400">
              {tickerStatus === "error" ? t("noData") : `≈ $${fmt(price)}`}
            </span>
          </div>
          <div className="hidden md:flex items-center gap-4">
          {[
            { label: t("indexPrice"), value: fmt(ticker?.indexPrice) },
            {
              label: t("change24h"),
              value:
                ticker?.changePct !== undefined
                  ? `${ticker.changePct >= 0 ? "+" : ""}${ticker.changePct.toFixed(2)}%`
                  : "—",
              color: ticker?.changePct >= 0 ? "text-green-500" : "text-red-500",
            },
            { label: t("high24h"), value: fmt(ticker?.high24h) },
            { label: t("low24h"), value: fmt(ticker?.low24h) },
            {
              label: t("turnover24h"),
              value: ticker?.turnover24h ? fmt2(ticker.turnover24h) : "—",
            },
            {
              label: t("fundingCountdown"),
              value: isFutures
                ? `${ticker?.fundingRate?.toFixed(4) ?? "—"}% / ${fmtCountdown(fundingLeft)}`
                : "—",
              color: "text-blue-800",
            },
          ].map((item) => (
            <div key={item.label} className="flex flex-col">
              <span className="text-[11px] text-slate-500">{item.label}</span>
              <span className={`text-[12px] ${item.color || "text-slate-200"}`}>{item.value}</span>
            </div>
          ))}
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="flex bg-slate-800 rounded overflow-hidden text-[11px]">
            {["en", "de"].map((l) => (
              <button
                key={l}
                onClick={() => changeLang(l)}
                disabled={langBusy}
                className={`px-2 py-1 ${lang === l ? "bg-blue-800 text-white font-bold" : "text-slate-400 hover:text-slate-200"} ${langBusy ? "opacity-60 cursor-wait" : ""}`}
              >
                {langBusy && lang !== l ? "…" : l.toUpperCase()}
              </button>
            ))}
          </div>
          <button
            onClick={openAssets}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-800 border border-slate-700 hover:border-[#1e40af] hover:bg-slate-700 transition-colors cursor-pointer group"
            title={t("assetsTitle")}
          >
            <i className="pi pi-wallet text-blue-800 text-[12px]"></i>
            <span className="text-slate-50 font-bold text-[13px] tabular-nums">{fmt2(balance + marginEquity)}</span>
            <span className="text-slate-500 text-[11px]">USDT</span>
            <i className="pi pi-chevron-right text-slate-500 group-hover:text-[#1e40af] text-[10px] ml-0.5"></i>
          </button>
          <div className="flex items-center gap-2 text-slate-300">
            <div className="relative">
              <button
                onClick={() => setProfileOpen((v) => !v)}
                className="flex items-center gap-2 hover:text-slate-100 transition-colors"
                title={t("profileWord")}
              >
                <i className="pi pi-user text-slate-500"></i>
                <span>{me.username}</span>
                {isAdmin && <span className="text-blue-800 text-[10px] border border-blue-800 rounded px-1">{t("admin")}</span>}
                <i className={`pi pi-chevron-down text-slate-500 text-[10px] transition-transform ${profileOpen ? "rotate-180" : ""}`}></i>
              </button>
              {profileOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setProfileOpen(false)}></div>
                  <div className="absolute right-0 mt-2 z-40 w-64 bg-slate-900 border border-slate-700 rounded-lg shadow-xl py-1 text-sm">
                    <div className="px-4 py-3 border-b border-slate-800">
                      <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-[#1e40af]/20 flex items-center justify-center">
                          <i className="pi pi-user text-blue-400"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="text-slate-100 font-semibold truncate">{me.username}</div>
                          {isAdmin && <div className="text-blue-400 text-[11px]">{t("admin")}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="px-4 py-2 space-y-2">
                      <div>
                        <div className="text-slate-600 text-[11px]">{t("emailWord")}</div>
                        <div className="text-slate-200 break-all">{me.email || "—"}</div>
                      </div>
                      <div>
                        <div className="text-slate-600 text-[11px]">{t("accountId")}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-200 tabular-nums">{me.id || "—"}</span>
                          {me.id && (
                            <button
                              onClick={() => {
                                navigator.clipboard?.writeText(String(me.id));
                                showToast(t("accountId") + ": " + me.id);
                              }}
                              className="text-slate-500 hover:text-blue-400"
                              title={t("accountId")}
                            >
                              <i className="pi pi-copy text-xs"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-slate-800 mt-1">
                      <button
                        onClick={() => {
                          logoutUser();
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-slate-300 hover:bg-slate-800 hover:text-red-400"
                      >
                        <i className="pi pi-sign-out"></i>
                        {t("logout")}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="hidden sm:block text-slate-400 text-xs">{new Date().toISOString().slice(11, 19)} UTC</div>
        </div>
      </header>
  );

  // Переключатель «График / Торговля» для мобильных. На ПК не используется.
  const mobilePaneSwitcher = (
        <div className="flex gap-1 p-2 bg-[#010208] border-b border-slate-800">
          {[["chart", t("chart")], ["trade", t("trade")]].map(([id, label]) => (
            <button
              key={id}
              onClick={() => setMobilePane(id)}
              className={`flex-1 py-2 rounded text-sm font-bold ${
                mobilePane === id ? "bg-blue-800 text-white" : "bg-slate-900 text-slate-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
  );

  // На телефоне/планшете шапка + переключатель показываются под графиком (над позициями)
  // и в панели торговли — чтобы переключатель всегда был доступен.
  const mobileTopBlock = !isDesktop ? (
    <>
      {tickerHeader}
      {mobilePaneSwitcher}
    </>
  ) : null;

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#010208] text-slate-300 font-sans text-xs antialiased select-none">
      {toast && (
        <div
          className={`fixed top-14 right-4 z-[100] px-4 py-2 rounded shadow-lg text-xs border ${
            toast.isErr
              ? "bg-red-950 border-red-800 text-red-200"
              : "bg-slate-800 border-slate-700 text-slate-100"
          }`}
        >
          {toast.msg}
        </div>
      )}

      {/* На ПК шапка сверху; на мобильных она переносится вниз под график */}
      {isDesktop && tickerHeader}

      {/* Основной контент */}
      <div className={`flex gap-3 p-2 sm:p-3 w-full max-w-full ${isDesktop ? "h-[calc(100vh-48px)]" : "flex-col min-h-[calc(100vh-96px)]"}`}>
        {/* Левая часть (График + Инструменты) */}
        <div
          className={`flex-1 flex bg-slate-900 rounded border border-slate-800 overflow-hidden min-w-0 ${
            !isDesktop && mobilePane !== "chart" ? "hidden" : ""
          } ${!isDesktop ? "min-h-[70vh]" : ""}`}
        >
          <aside className={`w-10 bg-[#010208] border-r border-slate-800 flex-col items-center py-3 gap-2 text-slate-500 shrink-0 ${isDesktop ? "flex" : "hidden"}`}>
            {DRAW_TOOLS.map((tool) => (
              <button
                key={tool.id}
                onClick={() => {
                  setActiveTool(tool.id);
                  tvRef.current?.selectTool?.(tool.id);
                  if (tool.id !== "cursor" && tool.id !== "eraser") showToast(`${t(tool.titleKey)} ${t("tDrawHint")}`);
                  if (tool.id === "eraser") showToast(t("tEraserHint"));
                }}
                className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                  activeTool === tool.id
                    ? "bg-blue-800/40 text-blue-800"
                    : "hover:text-blue-800"
                }`}
                title={t(tool.titleKey)}
              >
                {tool.svg === "noDraw" ? (
                  // Отключить рисование: знак запрета (круг с диагональной чертой)
                  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none"
                    stroke={activeTool === tool.id ? "#ef4444" : "currentColor"} strokeWidth="2.2"
                    strokeLinecap="round">
                    <circle cx="12" cy="12" r="9" />
                    <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" />
                  </svg>
                ) : tool.svg === "ray" ? (
                  // Луч: точка-начало + прямая со стрелкой (можно тянуть в любую сторону)
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none"
                    stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="4" cy="20" r="1.6" fill="currentColor" stroke="none" />
                    <line x1="5" y1="19" x2="20" y2="4" />
                    <polyline points="14 4 20 4 20 10" />
                  </svg>
                ) : (
                  <i className={`pi ${tool.icon} text-base`}></i>
                )}
              </button>
            ))}
            <span className="w-5 h-px bg-slate-800 my-1"></span>
            {/* Выбор цвета рисования */}
            <div className="relative">
              <button
                onClick={() => setColorMenuOpen((o) => !o)}
                className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-800 transition-colors"
                title={t("toolColor")}
              >
                <span
                  className="w-4 h-4 rounded-full border border-slate-600"
                  style={{ backgroundColor: drawColor }}
                ></span>
              </button>
              {colorMenuOpen && (
                <div className="absolute left-9 top-0 z-30 bg-slate-900 border border-slate-700 rounded-lg p-3 grid grid-cols-4 gap-3 shadow-xl">
                  {DRAW_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => {
                        setDrawColor(c);
                        tvRef.current?.setColor?.(c);
                        setColorMenuOpen(false);
                      }}
                      className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${
                        drawColor === c ? "border-white ring-2 ring-white/30" : "border-slate-600"
                      }`}
                      style={{ backgroundColor: c }}
                      title={c}
                    ></button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={() => setPickerOpen((o) => !o)}
              className="w-7 h-7 flex items-center justify-center rounded hover:text-blue-800 transition-colors"
              title={t("toolSearch")}
            >
              <i className="pi pi-search text-base"></i>
            </button>
            <button
              onClick={() => {
                tvRef.current?.removeAllShapes?.();
                showToast(t("tDrawingsCleared"));
              }}
              className="w-7 h-7 flex items-center justify-center rounded hover:text-red-400 transition-colors"
              title={t("toolTrash")}
            >
              <i className="pi pi-trash text-base"></i>
            </button>
          </aside>

          <main className="flex-1 flex flex-col min-w-0">
            <div className="h-10 shrink-0 border-b border-slate-800 flex items-center px-2 sm:px-3 gap-3 sm:gap-4 text-slate-400 overflow-x-auto no-scrollbar">
              {[["График", t("chart")], ["Обзор", t("overview")], ["Данные", t("data")], ["Лента новостей", t("news")]].map(([tab, label]) => (
                <button
                  key={tab}
                  onClick={() => setChartTab(tab)}
                  className={
                    "whitespace-nowrap shrink-0 " +
                    (chartTab === tab
                      ? "font-bold text-slate-50 border-b-2 border-blue-800 pb-2"
                      : "hover:text-slate-200 pb-2")
                  }
                >
                  {label}
                </button>
              ))}
              <span className="w-px h-4 bg-slate-700 shrink-0"></span>
              {TIMEFRAMES.map((time) => (
                <button
                  key={time}
                  onClick={() => setTimeframe(time)}
                  className={
                    "whitespace-nowrap shrink-0 " +
                    (timeframe === time ? "text-blue-800 font-bold" : "hover:text-slate-200")
                  }
                >
                  {time}
                </button>
              ))}
              <div className="relative ml-auto shrink-0 pl-2">
                <i
                  className={`pi pi-cog cursor-pointer ${chartSettingsOpen ? "text-blue-800" : "text-slate-600 hover:text-slate-300"}`}
                  title={t("tChartSettings")}
                  onClick={() => setChartSettingsOpen((v) => !v)}
                ></i>
                {chartSettingsOpen && (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setChartSettingsOpen(false)}></div>
                    <div className="absolute right-0 mt-2 z-30 w-56 bg-slate-900 border border-slate-700 rounded-lg shadow-xl py-1 text-sm">
                      <button
                        onClick={() => {
                          const next = !showVolume;
                          setShowVolume(next);
                          tvRef.current?.setVolumeVisible(next);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800"
                      >
                        <span>{t("csVolume")}</span>
                        <i className={`pi ${showVolume ? "pi-check-circle text-blue-800" : "pi-circle text-slate-600"}`}></i>
                      </button>
                      <button
                        onClick={() => {
                          const next = !logScale;
                          setLogScale(next);
                          tvRef.current?.setLogScale(next);
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800"
                      >
                        <span>{t("csLogScale")}</span>
                        <i className={`pi ${logScale ? "pi-check-circle text-blue-800" : "pi-circle text-slate-600"}`}></i>
                      </button>
                      <button
                        onClick={() => {
                          tvRef.current?.fitContent();
                          setChartSettingsOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-800"
                      >
                        <i className="pi pi-window-maximize text-slate-400"></i>
                        <span>{t("csFit")}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex-1 bg-[#010208] relative overflow-y-auto">
              {chartTab === "График" && (
                <TVChart ref={tvRef} symbol={symbol} timeframe={timeframe} livePrice={price} onToolChange={setActiveTool} />
              )}
              {chartTab === "Обзор" && <OverviewTab ticker={ticker} displaySymbol={displaySymbol} isFutures={isFutures} t={t} />}
              {chartTab === "Данные" && <DataTab ticker={ticker} price={price} t={t} />}
              {chartTab === "Лента новостей" && <NewsFeedTab lang={lang} t={t} />}
            </div>

            {/* На телефоне/планшете: тикер + переключатель под графиком, над панелью позиций */}
            {mobileTopBlock}

            {/* Разделитель: тянуть вверх/вниз, чтобы менять высоту нижней панели */}
            <div
              onMouseDown={startDrag("bottom")}
              onTouchStart={startDrag("bottom")}
              onDoubleClick={() => setBottomHeight(BOTTOM_DEFAULT)}
              title={t("dragH")}
              className="h-1.5 shrink-0 cursor-row-resize bg-slate-800 hover:bg-blue-800 transition-colors"
            />
            {/* Панель ордеров/позиций/истории внизу графика */}
            <div style={{ height: bottomHeight }} className="shrink-0 border-t border-slate-800 bg-[#010208] px-2 sm:px-3 py-2 overflow-y-auto overflow-x-auto">
              <div className="flex gap-3 sm:gap-4 mb-2 border-b border-slate-800 overflow-x-auto no-scrollbar">
                {bottomTabs.map(([tab, label]) => (
                  <button
                    key={tab}
                    onClick={() => setBottomTab(tab)}
                    className={
                      "whitespace-nowrap shrink-0 text-xs sm:text-sm " +
                      (bottomTab === tab
                        ? "text-slate-50 font-bold border-b-2 border-blue-800 pb-1"
                        : "text-slate-500 hover:text-slate-300 pb-1")
                    }
                  >
                    {label}
                    {tab === "Открытые ордера" && openOrders.length > 0 ? ` (${openOrders.length})` : ""}
                    {tab === "Позиции" && positions.length > 0 ? ` (${positions.length})` : ""}
                    {tab === "Позиции юзеров" && adminPositions.reduce((n, a) => n + a.positions.length, 0) > 0
                      ? ` (${adminPositions.reduce((n, a) => n + a.positions.length, 0)})`
                      : ""}
                  </button>
                ))}
              </div>

              {bottomTab === "Открытые ордера" &&
                (openOrders.length === 0 ? (
                  <div className="text-center text-slate-700 py-6">{t("noOpenOrders")}</div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="font-normal">{t("pairCol")}</th>
                        <th className="font-normal">{t("typeCol")}</th>
                        <th className="font-normal">{t("sideCol")}</th>
                        <th className="font-normal">{t("priceWord")}</th>
                        <th className="font-normal">TP</th>
                        <th className="font-normal">SL</th>
                        <th className="font-normal">{t("qtyCol")}</th>
                        <th className="font-normal"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {openOrders.map((o) => (
                        <tr key={o.id} className="border-t border-slate-900">
                          <td className="py-1 text-slate-300">
                            {o.symbol.includes(":") ? o.symbol.split(":")[1] : o.symbol}
                          </td>
                          <td>{t(o.type)}</td>
                          <td className={o.dir === "buy" ? "text-green-500" : "text-red-500"}>
                            {o.dir === "buy" ? t("long") : t("short")}
                          </td>
                          <td>{fmt(o.limitPrice)}</td>
                          <td>{o.tp ? fmt(o.tp) : "—"}</td>
                          <td>{o.sl ? fmt(o.sl) : "—"}</td>
                          <td>{fmtQty(o.qty)}</td>
                          <td>
                            <button onClick={() => cancelOrder(o.id)} className="text-slate-500 hover:text-red-400">
                              {t("cancel")}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}

              {bottomTab === "Позиции" &&
                (positions.length === 0 ? (
                  <div className="text-center text-slate-700 py-6">{t("noPositions")}</div>
                ) : (
                  <div className="overflow-x-auto">
                  <table className="w-full text-left whitespace-nowrap text-xs">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="font-normal pr-4">{t("posContract")}</th>
                        <th className="font-normal pr-4">{t("qtyCol")}</th>
                        <th className="font-normal pr-4">{t("posValue")}</th>
                        <th className="font-normal pr-4">{t("posEntryPrice")}</th>
                        <th className="font-normal pr-4">{t("posMark")}</th>
                        <th className="font-normal pr-4">{t("posLiq")}</th>
                        <th className="font-normal pr-4">{t("posBreakeven")}</th>
                        <th className="font-normal pr-4">{t("posIM")}</th>
                        <th className="font-normal pr-4">{t("posMM")}</th>
                        <th className="font-normal pr-4">{t("posUpnl")}</th>
                        <th className="font-normal pr-4">{t("posRealized")}</th>
                        <th className="font-normal pr-2 text-right">{t("posCloseAs")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {positions.map((p) => {
                        const MAINT_RATE = 0.005; // поддерживающая маржа 0.5%
                        const mark = priceOf(p.symbol);
                        const long = p.dir === "buy";
                        const notionalEntry = p.qty * p.entry;           // номинал по входу
                        const notionalMark = p.qty * (mark ?? p.entry);  // текущая стоимость
                        const im = notionalEntry / p.leverage;           // начальная маржа
                        const mm = notionalMark * MAINT_RATE;            // поддерживающая маржа
                        const pl = pnlOf(p);                             // нереализ. P&L
                        const roi = im > 0 ? (pl / im) * 100 : 0;        // ROI от маржи
                        // цена ликвидации (изолированная, приближённо)
                        const liq = long
                          ? p.entry * (1 - 1 / p.leverage + MAINT_RATE)
                          : p.entry * (1 + 1 / p.leverage - MAINT_RATE);
                        // цена безубыточности с учётом комиссий на вход+выход
                        const be = long
                          ? p.entry * (1 + 2 * TAKER_FEE)
                          : p.entry * (1 - 2 * TAKER_FEE);
                        // «Realized» показываем как чистый результат, если закрыть сейчас:
                        // нереализованный PnL минус комиссии за вход и выход.
                        const entryFee = notionalEntry * TAKER_FEE;
                        const closeFee = notionalMark * TAKER_FEE;
                        const realized = pl - entryFee - closeFee;
                        return (
                          <React.Fragment key={p.id}>
                          <tr className="border-t border-slate-900 align-top">
                            <td className="py-2 pr-4">
                              <div className={long ? "text-green-500 font-semibold" : "text-red-500 font-semibold"}>
                                {p.symbol.includes(":") ? p.symbol.split(":")[1] : p.symbol}
                              </div>
                              <div className="text-slate-600 text-[11px]">
                                {t("isolated")} {Number(p.leverage).toFixed(2)}x
                              </div>
                            </td>
                            <td className={`pr-4 ${long ? "text-green-500" : "text-red-500"}`}>
                              {fmtQty(p.qty)}
                            </td>
                            <td className="pr-4 text-slate-300">{fmt2(notionalMark)} USDT</td>
                            <td className="pr-4 text-slate-300">{fmt(p.entry)}</td>
                            <td className="pr-4 text-slate-300">{mark !== null ? fmt(mark) : "—"}</td>
                            <td className="pr-4 text-orange-400">{fmt(liq)}</td>
                            <td className="pr-4 text-slate-300">{fmt(be)}</td>
                            <td className="pr-4 text-slate-300">{fmt2(im)} USDT</td>
                            <td className="pr-4 text-slate-300">{fmt2(mm)} USDT</td>
                            <td className="pr-4">
                              <div className={pl >= 0 ? "text-green-500" : "text-red-500"}>
                                {pl >= 0 ? "+" : ""}{fmt2(pl)} USDT
                              </div>
                              <div className={pl >= 0 ? "text-green-500 text-[11px]" : "text-red-500 text-[11px]"}>
                                ({roi >= 0 ? "+" : ""}{roi.toFixed(2)}%)
                              </div>
                            </td>
                            <td className={`pr-4 ${realized >= 0 ? "text-green-500" : "text-red-500"}`}>
                              {realized >= 0 ? "+" : ""}{fmt2(realized)} USDT
                            </td>
                            <td className="pr-2">
                              <div className="flex gap-2 justify-end items-center">
                                <button
                                  onClick={() => setTpSlEditId(tpSlEditId === p.id ? null : p.id)}
                                  className={`px-2 py-1 rounded border ${tpSlEditId === p.id ? "border-blue-800 text-blue-400" : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
                                  title={t("editTpSl")}
                                >
                                  TP/SL
                                </button>
                                <button
                                  onClick={() => closePosition(p.id, "rManual", true)}
                                  disabled={posBusy}
                                  className="px-3 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {t("closeLimit")}
                                </button>
                                <button
                                  onClick={() => closePosition(p.id, "rManual", false)}
                                  disabled={posBusy}
                                  className="px-3 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  {t("closeMarket")}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {tpSlEditId === p.id && (
                            <tr className="bg-[#010208]">
                              <td colSpan={12} className="px-3 py-3">
                                <div className="flex flex-wrap items-end gap-4">
                                  <div>
                                    <div className="text-slate-500 text-[11px] mb-1">TP ({t("posValue")} ≥ / ≤ {fmt(p.entry)})</div>
                                    <input
                                      defaultValue={p.tp ?? ""}
                                      placeholder="—"
                                      onBlur={(e) => updatePositionTPSL(p.id, "tp", e.target.value)}
                                      className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-28 text-right outline-none focus:border-blue-800"
                                    />
                                  </div>
                                  <div>
                                    <div className="text-slate-500 text-[11px] mb-1">SL</div>
                                    <input
                                      defaultValue={p.sl ?? ""}
                                      placeholder="—"
                                      onBlur={(e) => updatePositionTPSL(p.id, "sl", e.target.value)}
                                      className="bg-slate-900 border border-slate-700 rounded px-2 py-1 w-28 text-right outline-none focus:border-blue-800"
                                    />
                                  </div>
                                  <button
                                    onClick={() => setTpSlEditId(null)}
                                    className="px-3 py-1.5 rounded bg-[#1e40af] hover:bg-[#172554] text-white font-semibold"
                                  >
                                    {t("saveWord")}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                ))}

              {bottomTab === "История Ордеров" &&
                (orderLog.length === 0 ? (
                  <div className="text-center text-slate-700 py-6">{t("emptyOrderHistory")}</div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="font-normal">{t("timeCol")}</th>
                        <th className="font-normal">{t("pairCol")}</th>
                        <th className="font-normal">{t("typeCol")}</th>
                        <th className="font-normal">{t("sideCol")}</th>
                        <th className="font-normal">{t("priceWord")}</th>
                        <th className="font-normal">{t("qtyCol")}</th>
                        <th className="font-normal">{t("statusCol")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderLog.map((o) => (
                        <tr key={o.id} className="border-t border-slate-900">
                          <td className="py-1">{o.time.toLocaleTimeString()}</td>
                          <td className="text-slate-300">{o.symbol?.includes(":") ? o.symbol.split(":")[1] : o.symbol ?? "—"}</td>
                          <td>{t(o.type)}</td>
                          <td className={o.dir === "buy" ? "text-green-500" : "text-red-500"}>
                            {o.dir === "buy" ? t("buyWord") : t("sellWord")}
                          </td>
                          <td>{fmt(o.price)}</td>
                          <td>{fmtQty(o.qty)}</td>
                          <td className="text-slate-400">{t(o.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}

              {bottomTab === "История торговли" &&
                (tradeLog.length === 0 ? (
                  <div className="text-center text-slate-700 py-6">{t("emptyTradeHistory")}</div>
                ) : (
                  <table className="w-full text-left">
                    <thead className="text-slate-500">
                      <tr>
                        <th className="font-normal">{t("timeCol")}</th>
                        <th className="font-normal">{t("pairCol")}</th>
                        <th className="font-normal">{t("sideCol")}</th>
                        <th className="font-normal">{t("qtyCol")}</th>
                        <th className="font-normal">{t("entryCol")}</th>
                        <th className="font-normal">{t("exitCol")}</th>
                        <th className="font-normal">PnL</th>
                        <th className="font-normal">{t("feeCol")}</th>
                        <th className="font-normal">{t("reasonCol")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tradeLog.map((tr) => (
                        <tr key={tr.id} className="border-t border-slate-900">
                          <td className="py-1">{tr.time.toLocaleTimeString()}</td>
                          <td className="text-slate-300">{tr.symbol?.includes(":") ? tr.symbol.split(":")[1] : tr.symbol ?? "—"}</td>
                          <td className={tr.dir === "buy" ? "text-green-500" : "text-red-500"}>
                            {tr.dir === "buy" ? t("long") : t("short")}
                          </td>
                          <td>{fmtQty(tr.qty)}</td>
                          <td>{fmt(tr.entry)}</td>
                          <td>{tr.exit ? fmt(tr.exit) : "—"}</td>
                          <td className={tr.pnl === null ? "text-slate-500" : tr.pnl >= 0 ? "text-green-500" : "text-red-500"}>
                            {tr.pnl === null ? "—" : `${tr.pnl >= 0 ? "+" : ""}${fmt2(tr.pnl)}`}
                          </td>
                          <td className="text-blue-800">{tr.fee ? fmt2(tr.fee) : "—"}</td>
                          <td className="text-slate-400">{t(tr.reason)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}

              {bottomTab === "P&L" && (
                <div className="py-2">
                  <div className="mb-2">
                    <span className="text-slate-500">{t("totalPnl")}: </span>
                    <span className={totalRealizedPnl >= 0 ? "text-green-500" : "text-red-500"}>
                      {totalRealizedPnl >= 0 ? "+" : ""}
                      {fmt2(totalRealizedPnl)} USDT
                    </span>
                  </div>
                  {tradeLog.filter((tr) => tr.pnl !== null).length === 0 ? (
                    <div className="text-center text-slate-700 py-4">{t("noClosedTrades")}</div>
                  ) : (
                    <>
                      <TradeStatsCards stats={calcTradeStats(tradeLog.filter((tr) => tr.pnl !== null))} t={t} />
                      <table className="w-full text-left">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="font-normal">{t("timeCol")}</th>
                          <th className="font-normal">{t("sideCol")}</th>
                          <th className="font-normal">PnL</th>
                          <th className="font-normal">{t("reasonCol")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tradeLog
                          .filter((tr) => tr.pnl !== null)
                          .map((tr) => (
                            <tr key={tr.id} className="border-t border-slate-900">
                              <td className="py-1">{tr.time.toLocaleTimeString()}</td>
                              <td className={tr.dir === "buy" ? "text-green-500" : "text-red-500"}>
                                {tr.dir === "buy" ? t("long") : t("short")}
                              </td>
                              <td className={tr.pnl >= 0 ? "text-green-500" : "text-red-500"}>
                                {tr.pnl >= 0 ? "+" : ""}
                                {fmt2(tr.pnl)}
                              </td>
                              <td className="text-slate-400">{tr.reason}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    </>
                  )}
                </div>
              )}

              {bottomTab === "Пользователи" && isAdmin && (
                <UsersAdminPanel users={users} error={usersError} onGrant={grantFunds} onToggleAdmin={toggleAdmin} t={t} />
              )}

              {bottomTab === "Позиции юзеров" && isAdmin && (
                <AdminPositionsPanel accounts={adminPositions} priceOf={priceOf} onClose={adminClosePosition} onSetTpSl={adminSetTpSl} t={t} />
              )}

              {bottomTab === "История юзеров" && isAdmin && (
                <AdminHistoryPanel accounts={adminHistory} t={t} />
              )}

              {bottomTab === "Транзакции юзеров" && isAdmin && (
                <>
                  <AdminDepositIbanEditor token={token} t={t} />
                  <AdminTransactionsPanel accounts={adminTransactions} onDecide={decideTransaction} t={t} />
                </>
              )}

              {bottomTab === "Займы" && (
                <div className="py-3 max-w-md">
                  <div className="text-slate-300 font-semibold mb-2">{t("loans")}</div>
                  <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <div className="text-slate-500 text-xs">{t("availToBorrow")}</div>
                      <div className="text-slate-50 font-bold">{fmt2(available)} USDT <span className="text-slate-500 text-xs">/ {fmtEur(available)} EUR</span></div>
                    </div>
                    <span className="text-slate-500 text-xs">{t("loanRate")}: 0.00%</span>
                  </div>
                  <p className="text-slate-600 text-xs mt-3">{t("loanNote")}</p>
                </div>
              )}

              {bottomTab === "Инструменты" && (
                <div className="py-3">
                  <div className="text-slate-300 font-semibold mb-3">{t("tools")}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
                    <button onClick={() => openFund("deposit")} className="bg-slate-900 hover:bg-slate-800 rounded-lg p-4 text-left border border-slate-800">
                      <i className="pi pi-arrow-down text-[#1e40af] text-lg"></i>
                      <div className="text-slate-100 font-semibold mt-2">{t("depositWord")}</div>
                    </button>
                    <button onClick={() => openFund("withdraw")} className="bg-slate-900 hover:bg-slate-800 rounded-lg p-4 text-left border border-slate-800">
                      <i className="pi pi-arrow-up text-[#1e40af] text-lg"></i>
                      <div className="text-slate-100 font-semibold mt-2">{t("withdrawWord")}</div>
                    </button>
                    <button onClick={() => setBottomTab("P&L")} className="bg-slate-900 hover:bg-slate-800 rounded-lg p-4 text-left border border-slate-800">
                      <i className="pi pi-chart-line text-[#1e40af] text-lg"></i>
                      <div className="text-slate-100 font-semibold mt-2">{t("pnlTab")}</div>
                    </button>
                    <button onClick={() => setBottomTab("История торговли")} className="bg-slate-900 hover:bg-slate-800 rounded-lg p-4 text-left border border-slate-800">
                      <i className="pi pi-history text-[#1e40af] text-lg"></i>
                      <div className="text-slate-100 font-semibold mt-2">{t("tradeHistory")}</div>
                    </button>
                    <button onClick={openAssets} className="bg-slate-900 hover:bg-slate-800 rounded-lg p-4 text-left border border-slate-800">
                      <i className="pi pi-wallet text-[#1e40af] text-lg"></i>
                      <div className="text-slate-100 font-semibold mt-2">{t("assets")}</div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </main>
        </div>

        {isDesktop && (
          <div
            onMouseDown={startDrag("right")}
            onTouchStart={startDrag("right")}
            onDoubleClick={() => setRightWidth(RIGHT_DEFAULT)}
            title={t("dragW")}
            className="w-1.5 shrink-0 cursor-col-resize rounded bg-slate-800 hover:bg-blue-800 transition-colors"
          />
        )}

        {/* Правая часть (Стакан + Ордера) */}
        <aside
          style={isDesktop ? { width: rightWidth } : undefined}
          className={`shrink-0 flex flex-col gap-3 bg-[#010208] rounded border border-slate-800 min-w-0 ${
            isDesktop ? "" : "w-full max-w-full " + (mobilePane !== "trade" ? "hidden" : "")
          }`}
        >
          <section style={{ height: bookHeight }} className="shrink-0 flex flex-col">
            <header className="h-10 px-4 flex items-center border-b border-slate-800 justify-between">
              <h2 className="font-bold text-slate-50">{t("orderBook")}</h2>
              <div className="flex items-center gap-2 text-slate-500">
                <i
                  className={`pi pi-align-justify border rounded p-1 cursor-pointer ${bookView === "all" ? "text-blue-800 border-blue-800" : "border-slate-700 hover:text-slate-200"}`}
                  onClick={() => setBookView("all")}
                  title={t("viewAll")}
                ></i>
                <i
                  className={`pi pi-align-top border rounded p-1 cursor-pointer ${bookView === "asks" ? "text-red-400 border-red-500" : "border-slate-700 hover:text-slate-200"}`}
                  onClick={() => setBookView("asks")}
                  title={t("viewSells")}
                ></i>
                <i
                  className={`pi pi-align-bottom border rounded p-1 cursor-pointer ${bookView === "bids" ? "text-green-400 border-green-500" : "border-slate-700 hover:text-slate-200"}`}
                  onClick={() => setBookView("bids")}
                  title={t("viewBuys")}
                ></i>
                <span className="bg-[#010208] border border-slate-800 px-2 py-1 rounded">0.01</span>
              </div>
            </header>
            <div className="grid grid-cols-3 px-4 py-1 text-slate-500 text-right">
              <span>{t("price")}</span>
              <span>{t("qtyCol")}</span>
              <span>{t("totalCol")}</span>
            </div>
            <div className="h-10 flex items-center justify-center text-red-500 font-bold border-y border-slate-800 bg-[#010208]">
              <i className="pi pi-arrow-down mr-2"></i>
              {fmt(price)} <span className="text-slate-500 mx-2">/</span> {fmt(ticker?.indexPrice)}{" "}
              <i className="pi pi-arrow-up ml-2"></i>
            </div>
            <div className="flex-1 overflow-hidden grid grid-cols-3 px-4 text-right gap-y-0.5 font-mono relative">
              <div className="absolute inset-y-0 right-0 left-1/2 bg-red-950/30 pointer-events-none"></div>
              <div className="absolute inset-y-0 right-0 left-3/4 bg-red-950/50 pointer-events-none"></div>

              {bookView !== "bids" && asks.map((a, i) => (
                <React.Fragment key={"a" + i}>
                  <button onClick={() => fillFromBook(a.price)} className="text-red-500 text-right hover:underline">
                    {fmt(a.price)}
                  </button>
                  <span className="text-slate-300">{fmtQty(a.qty)}</span>
                  <span className="text-slate-600">{fmtQty(a.total)}</span>
                </React.Fragment>
              ))}
              {bookView !== "asks" && bids.map((b, i) => (
                <React.Fragment key={"b" + i}>
                  <button onClick={() => fillFromBook(b.price)} className="text-green-500 text-right hover:underline">
                    {fmt(b.price)}
                  </button>
                  <span className="text-slate-300">{fmtQty(b.qty)}</span>
                  <span className="text-slate-600">{fmtQty(b.total)}</span>
                </React.Fragment>
              ))}
            </div>
          </section>

          {/* Разделитель: тянуть, чтобы менять пропорцию стакан/меню открытия позиции */}
          <div
            onMouseDown={startDrag("book")}
            onTouchStart={startDrag("book")}
            onDoubleClick={() => setBookHeight(BOOK_DEFAULT)}
            title={t("dragB")}
            className="h-1.5 shrink-0 -my-1.5 cursor-row-resize bg-slate-800 hover:bg-blue-800 transition-colors rounded"
          />

          {/* Панель торговли */}
          <section className="flex-1 flex flex-col p-4 overflow-y-auto min-h-0">
            <div className="flex gap-4 mb-3 border-b border-slate-800">
              {[t("limit"), t("market"), t("limitOco")].map((tab, i) => (
                <button
                  key={i}
                  onClick={() => setOrderTypeTab(i)}
                  className={
                    orderTypeTab === i
                      ? "text-slate-50 font-bold border-b-2 border-blue-800 pb-1"
                      : "text-slate-500 hover:text-slate-300 pb-1"
                  }
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex justify-between text-slate-400">
                <span>{t("available")}</span>
                <span>{fmt2(available)} USDT <span className="text-slate-500 text-xs">/ {fmtEur(available)} EUR</span></span>
              </div>

              <div className="h-9 bg-[#010208] border border-slate-700 rounded flex items-center px-3 justify-between relative">
                <input
                  value={orderTypeTab === 1 ? (price !== null ? price.toFixed(decimalsFor(price)) : "—") : limitPrice}
                  disabled={orderTypeTab === 1}
                  onChange={(e) => setLimitPrice(sanitizeNumber(e.target.value))}
                  className="bg-transparent text-slate-200 w-28 outline-none disabled:text-slate-500"
                />
                <span className="text-slate-500">
                  USDT{" "}
                  <span className="text-slate-700 ml-1">
                    ≈ ${orderTypeTab === 1 ? fmt(price) : fmt(Number(limitPrice) || price)}
                  </span>
                </span>
                <div className="absolute left-0 bottom-0 h-0.5 bg-blue-800 w-3/5"></div>
              </div>

              <label className="h-9 bg-[#010208] border border-slate-700 rounded flex items-center px-3 gap-2 text-slate-400 cursor-text">
                <span className="whitespace-nowrap">{t("quantity")}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck="false"
                  value={qty}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 min-w-0 bg-transparent text-right text-slate-200 outline-none placeholder-slate-600"
                />
                <span className="text-slate-600 text-[11px] whitespace-nowrap">{displaySymbol.replace("USDT", "")}</span>
              </label>

              <label className="h-9 bg-[#010208] border border-slate-700 rounded flex items-center px-3 gap-2 text-slate-400 cursor-text">
                <span className="whitespace-nowrap">{t("amount")}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  spellCheck="false"
                  value={usdAmount}
                  onChange={(e) => handleUsdChange(e.target.value)}
                  placeholder="0.00"
                  className="flex-1 min-w-0 bg-transparent text-right text-slate-200 outline-none placeholder-slate-600"
                />
                <span className="text-slate-600 text-[11px] whitespace-nowrap">USDT</span>
              </label>

              {/* Плечо — сразу под полем «Сумма», в скромном (неброском) виде */}
              <div className="relative">
                <button
                  onClick={() => setLeveragePanelOpen((o) => !o)}
                  className="w-full flex items-center justify-between px-3 py-1.5 rounded border border-slate-800 bg-[#010208] text-slate-400 hover:border-slate-600 transition-colors"
                  title={t("chooseLev")}
                >
                  <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    <i className="pi pi-sliders-h text-[11px]"></i>
                    {t("isolated")}
                  </span>
                  <span className="flex items-center gap-1 text-[13px] text-slate-300">
                    {leverage.toFixed(2)}x
                    <i className="pi pi-chevron-down text-[10px]"></i>
                  </span>
                </button>
                {leveragePanelOpen && (
                  <div className="absolute right-0 top-9 z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl p-3 w-56">
                    <div className="text-slate-500 text-[11px] pb-2">{t("leverage")} (1–100x)</div>

                    {/* Ручной ввод любого значения */}
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="number"
                        min={1}
                        max={100}
                        step={1}
                        value={leverage}
                        onChange={(e) => {
                          let v = parseFloat(e.target.value);
                          if (!isFinite(v)) v = 1;
                          v = Math.min(100, Math.max(1, v));
                          setLeverage(v);
                        }}
                        className="flex-1 bg-[#010208] border border-slate-700 rounded px-2 py-1 text-sm text-slate-100 outline-none focus:border-blue-700"
                      />
                      <span className="text-slate-400 text-sm">x</span>
                    </div>

                    {/* Слайдер 1–100 */}
                    <input
                      type="range"
                      min={1}
                      max={100}
                      step={1}
                      value={leverage}
                      onChange={(e) => setLeverage(Number(e.target.value))}
                      className="w-full appearance-none h-1 bg-slate-700 rounded-full accent-blue-800 cursor-pointer mb-2"
                    />

                    {/* Быстрые пресеты */}
                    <div className="grid grid-cols-4 gap-1">
                      {LEVERAGES.map((lv) => (
                        <button
                          key={lv}
                          onClick={() => setLeverage(lv)}
                          className={`text-xs py-1 rounded ${
                            Number(leverage) === lv ? "bg-blue-800 text-white font-bold" : "bg-slate-900 text-slate-300 hover:bg-slate-700"
                          }`}
                        >
                          {lv}x
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setLeveragePanelOpen(false)}
                      className="w-full mt-2 bg-[#1e40af] hover:bg-[#172554] text-white text-xs font-semibold py-1.5 rounded"
                    >
                      {t("confirm")}
                    </button>
                  </div>
                )}
              </div>

              {/* Постоянная подсказка о нехватке маржи — видна сразу, не только по клику на Купить/Продать */}
              {Number(qty) > 0 && effPriceForCalc && (Number(qty) * effPriceForCalc) / leverage > available + 0.0000001 && (
                <div className="text-red-400 text-[11px] -mt-2">
                  {t("insuffWarn1")}{fmt2((Number(qty) * effPriceForCalc) / leverage)} USDT,
                  {" "}{t("insuffWarn2")} {fmt2(available)} USDT.
                  {balance > 0 ? ` ${t("insuffWarn3")}` : ` ${t("insuffWarn4")}`}
                </div>
              )}

              {/* Слайдер */}
              <div className="py-3 px-1.5 relative">
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={percent}
                  onChange={(e) => handlePercent(Number(e.target.value))}
                  className="w-full appearance-none h-1 bg-slate-700 rounded-full accent-blue-800 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 mt-1">
                  <span>0</span>
                  <span>{percent}%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* TP / SL — раскрывается прямо в сайдбаре без модала */}
              <button
                onClick={() => setTpSlModalOpen((v) => !v)}
                className={`border rounded p-2 flex items-center justify-between text-slate-400 hover:border-blue-800 transition-colors ${tpSlModalOpen ? "border-blue-800 text-blue-400" : "border-slate-800"}`}
              >
                <span>{t("tpSl")}</span>
                <span className="text-slate-300 text-[11px]">
                  {tpEnabled || slEnabled
                    ? `${tpEnabled ? `TP ${tpPrice}` : ""} ${slEnabled ? `SL ${slPrice}` : ""}`.trim()
                    : t("notSet")}
                  <span className="ml-2 text-[10px]">{tpSlModalOpen ? "▲" : "▼"}</span>
                </span>
              </button>
              <TpSlPanel
                open={tpSlModalOpen}
                onClose={() => setTpSlModalOpen(false)}
                price={effPriceForCalc}
                leverage={leverage}
                tpEnabled={tpEnabled}
                slEnabled={slEnabled}
                side={tpSlSide}
                tpPrice={tpPrice}
                slPrice={slPrice}
                onChange={(field, value) => {
                  if (field === "tpEnabled") setTpEnabled(value);
                  else if (field === "slEnabled") setSlEnabled(value);
                  else if (field === "tpPrice") setTpPrice(value);
                  else if (field === "slPrice") setSlPrice(value);
                  else if (field === "side") setTpSlSide(value);
                }}
                t={t}
              />

              <div className="grid grid-cols-2 gap-2 text-center">
                <button
                  onClick={() => placeOrder("buy")}
                  disabled={price === null || posBusy}
                  className="bg-green-600 hover:bg-green-500 active:scale-[0.98] transition text-white py-2 rounded font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {posBusy ? t("processing") : t("buyLong")}
                </button>
                <button
                  onClick={() => placeOrder("sell")}
                  disabled={price === null || posBusy}
                  className="bg-red-600 hover:bg-red-500 active:scale-[0.98] transition text-white py-2 rounded font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {posBusy ? t("processing") : t("sellShort")}
                </button>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-800 text-slate-400">
              <h3 className="text-slate-200 font-bold mb-2">{t("contractData")} {displaySymbol}</h3>
              <div className="flex justify-between py-0.5">
                <span>{t("expiry")}</span>
                <span className="text-slate-50">{isFutures ? t("perpWord") : t("spot")}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span>{t("indexPriceShort")}</span>
                <span className="text-slate-50">{fmt(ticker?.indexPrice)}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span>{t("priceWord")}</span>
                <span className="text-slate-50">{fmt(price)}</span>
              </div>
              <ContractDetails t={t} />
            </div>
          </section>

          {/* На телефоне/планшете: тикер + переключатель в самом низу панели, ниже всех блоков */}
          {!isDesktop && (
            <div className="-m-px mt-0 rounded-b">
              {tickerHeader}
              {mobilePaneSwitcher}
            </div>
          )}
        </aside>
      </div>


      {fundModal && (
        <FundModal
          mode={fundModal}
          balance={balance}
          token={token}
          onClose={closeFund}
          onSubmit={submitTransaction}
          t={t}
        />
      )}

      {assetsPageOpen && (
        <AssetsPage
          balance={balance}
          marginBalance={marginBalance}
          marginEquity={marginEquity}
          usedMargin={usedMargin}
          available={available}
          transactions={transactions}
          tradeLog={tradeLog}
          onDeposit={() => openFund("deposit")}
          onWithdraw={() => openFund("withdraw")}
          onTransfer={transferFunds}
          onClose={closeAssets}
          t={t}
        />
      )}

      {(langBusy || logoutBusy) && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#010208]/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-slate-600 border-t-blue-500 rounded-full animate-spin"></div>
            <span className="text-slate-400 text-sm">{t("processing")}</span>
          </div>
        </div>
      )}

      <link rel="stylesheet" href="https://unpkg.com/primeicons/primeicons.css" />
    </div>
  );
}

function ContractDetails({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="flex justify-between py-0.5 text-blue-800 w-full text-left">
        <span>
          {open ? t("hideWord") : t("showWord")}
          <i className={`pi pi-chevron-${open ? "up" : "down"} ml-1`}></i>
        </span>
      </button>
      {open && (
        <div className="text-slate-500 text-[11px] flex flex-col gap-1 mt-1">
          <div className="flex justify-between">
            <span>{t("minLev")}</span>
            <span className="text-slate-300">1.00x</span>
          </div>
          <div className="flex justify-between">
            <span>{t("maxLev")}</span>
            <span className="text-slate-300">100.00x</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Граница ошибок: если какой-то компонент упадёт при рендере, показываем
// понятное сообщение и кнопку перезагрузки — вместо «белого экрана».
// ============================================================================
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // Пишем в консоль для диагностики (F12 -> Console)
    console.error("UI crash:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#010208] text-slate-200 flex items-center justify-center p-6">
          <div className="max-w-lg w-full bg-slate-900 border border-slate-700 rounded-lg p-5">
            <div className="text-red-400 font-bold text-lg mb-2">Что-то пошло не так</div>
            <div className="text-slate-400 text-sm mb-3">
              Произошла ошибка при отрисовке интерфейса. Попробуйте перезагрузить страницу.
            </div>
            <pre className="text-[11px] text-slate-500 bg-[#010208] border border-slate-800 rounded p-2 overflow-auto max-h-40 whitespace-pre-wrap">
              {String(this.state.error?.message || this.state.error)}
            </pre>
            <button
              onClick={() => this.setState({ error: null })}
              className="mt-3 bg-blue-800 hover:bg-blue-800 text-white font-bold px-4 py-2 rounded"
            >
              Продолжить
            </button>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 ml-2 bg-slate-800 hover:bg-slate-700 text-slate-200 px-4 py-2 rounded"
            >
              Перезагрузить
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  // Подключаем фирменный шрифт (IBM Plex Sans — как на бирже) и слегка
  // увеличиваем базовый размер текста до «биржевого». Делается через <head>,
  // чтобы работать независимо от сборки.
  useEffect(() => {
    const pre1 = document.createElement("link");
    pre1.rel = "preconnect";
    pre1.href = "https://fonts.googleapis.com";
    const pre2 = document.createElement("link");
    pre2.rel = "preconnect";
    pre2.href = "https://fonts.gstatic.com";
    pre2.crossOrigin = "anonymous";
    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href =
      "https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap";

    const style = document.createElement("style");
    style.setAttribute("data-app-font", "1");
    style.textContent = `
      :root { font-size: 17px; }
      *:not(.pi):not([class*="pi-"]):not(i) {
        font-family: "IBM Plex Sans", -apple-system, BlinkMacSystemFont,
          "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
      }
      html, body, #root {
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
      }
    `;
    [pre1, pre2, font, style].forEach((el) => document.head.appendChild(el));
    return () => {
      [pre1, pre2, font, style].forEach((el) => el.remove());
    };
  }, []);

  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}

export default App;